import { Canvas, FabricImage, Group, FabricObject } from 'fabric'
import {
  createShadow,
  createContactShadow,
  createGlowShadow,
  getBottomCanvasPoints,
  getBottomShapeLabel,
  createDropShadow,
  generateAIShadows,
  getBottomWaveCurvePoint,
} from './shadowHelper'
import { PLACEMENT_TYPES, DIRECTIONS, PRODUCT_ASPECT_TYPES, BOX_ASPECT_RATIO_MIN } from '@/constants/positions'
import type { PlacementSpace, AspectCounts } from '@/constants/placementStrategies'
import { usePlacementStore } from '@/stores/placement'
import { useCanvasStore } from '@/stores/canvas'
import { v4 as uuidv4 } from 'uuid'
import { getRandomNumber } from './commonHelper'
import { getBottomPoint, detachContactShadow } from '@/helpers/shadowHelper'
import {
  reflowTextAfterScaling,
  reflowTextBasedOnCollisions,
  resetHeadlineSubheadlines,
} from './textHelper'
import { layerNames } from '@/constants/layers'
import { useAdSizeConfigStore } from '@/stores/adSizeConfig'
import { ASPECT_TYPE_ORDER_FOR_STAIRCASE, TOP_PLACEMENT_STYLE, getWidthSplitWithRotationPenaltyFor2Products, getWidthSplitWithRotationPenaltyFor3Products, getHeightSplitForPairWithRotationPenalty, getOverlapPercentageForStaircase, determineProductsPlacementStyleInTopPlacement, getOverlapPercentageForTopPlacement } from './ProductPlacementStratergyHelper'

// Get default placement area from config store
export function getDefaultPlacementArea(): PlacementSpace {
  return useAdSizeConfigStore().defaultPlacementArea
}

// Get baseline values from config store
function getFrontBaseBottom(): number {
  return useAdSizeConfigStore().frontBaseBottom
}

function getTopBaseBottom(): number {
  return useAdSizeConfigStore().topBaseBottom
}

function getPlacementBounds(boundsName: string): PlacementSpace {
  return useAdSizeConfigStore().getNamedBounds(boundsName)
}

const PLACEMENT_AREA = {
  top: 0,
  left: 0,
  right: 0,
  bottom: 0
}

export const loadPlacements = async (
  canvas: Canvas,
  productImages: FabricImage[],
  placement: string,
  mainShadow: string,
  lightDirection: string,
  angle: number = 0,
  aspectCounts: AspectCounts | null = null,
  shouldReflow: boolean = true,
  areAllEdibleProducts: boolean = false,
) => {
  if (angle > 0 && angle < 1.5) {
    angle = 1.5
  } else if (angle < 0 && angle > -1.5) {
    angle = -1.5
  }

  const tiltAngle: number = angle
  const tiltAngles: [number, number] = [angle, angle]
  const overlapStyle: string = 'edge'
  const canvasStore = useCanvasStore()

  resetCanvasImages(canvas, productImages)

  if (productImages.length == 1) {
    switch (placement) {
      case PLACEMENT_TYPES.FRONT:
        await renderFrontSingle(
          canvas,
          productImages[0],
          mainShadow,
          lightDirection,
          placement,
          tiltAngle,
        )
        break
      case PLACEMENT_TYPES.TOP:
        await renderTopSingle(
          canvas,
          productImages[0],
          mainShadow,
          lightDirection,
          placement,
          tiltAngle,
        )
        break
      case PLACEMENT_TYPES.FLOAT:
        await renderFloatSingle(
          canvas,
          productImages[0],
          mainShadow,
          lightDirection,
          placement,
          tiltAngle,
        )
        break
      case PLACEMENT_TYPES.CORNER:
        await renderCornerSingle(
          canvas,
          productImages[0],
          mainShadow,
          lightDirection,
          placement,
          tiltAngle,
        )
        break
    }
  } else if (productImages.length == 2) {
    switch (placement) {
      case PLACEMENT_TYPES.FRONT:
        await renderFrontPairPlacement(
          canvas,
          productImages,
          tiltAngle,
          lightDirection,
          placement,
          aspectCounts,
          areAllEdibleProducts || canvasStore.areAllEdibleProducts
        )
        break
      case PLACEMENT_TYPES.TOP:
        await renderTopPairPlacement(
          canvas,
          productImages,
          tiltAngles,
          overlapStyle,
          lightDirection,
          placement,
        )
        break
      case PLACEMENT_TYPES.FLOAT:
        // Change Overlap style to "float" and shadow
        await renderTopPairPlacement(
          canvas,
          productImages,
          tiltAngles,
          'float',
          lightDirection,
          placement,
        )
        break
      case PLACEMENT_TYPES.CORNER:
        await renderCornerPairPlacement(
          canvas,
          productImages,
          tiltAngle,
          lightDirection,
          placement,
          aspectCounts?.Horizontal,
        )
        break
      case PLACEMENT_TYPES.STACK:
        await renderStackPairPlacement(canvas, productImages, tiltAngle, lightDirection, placement)
        break
      // Disabled
      // case PLACEMENT_TYPES.VERTICAL:
      //   await renderVerticalPairPlacement(
      //     canvas,
      //     productImages,
      //     tiltAngle,
      //     lightDirection,
      //     placement,
      //   )
      //   break
      case PLACEMENT_TYPES.STAIRCASE:
        await renderStaircasePairPlacement(
          canvas,
          productImages,
          0, // Tilt angle set to 0
          lightDirection,
          placement,
          areAllEdibleProducts || canvasStore.areAllEdibleProducts
        )
        break
    }
  } else if (productImages.length == 3) {
    switch (placement) {
      case PLACEMENT_TYPES.FRONT:
        await renderFrontPlacement(
          canvas,
          productImages,
          mainShadow,
          lightDirection,
          PLACEMENT_TYPES.FRONT,
          angle,
          aspectCounts,
          areAllEdibleProducts || canvasStore.areAllEdibleProducts
        )
        break
      case PLACEMENT_TYPES.TOP:
        await renderTopPlacement(
          canvas,
          productImages,
          mainShadow,
          lightDirection,
          PLACEMENT_TYPES.TOP,
          angle,
          areAllEdibleProducts || canvasStore.areAllEdibleProducts,
        )
        break
      // Disabled
      // case PLACEMENT_TYPES.VERTICAL:
      //   await renderVerticalPlacement(
      //     canvas,
      //     productImages,
      //     mainShadow,
      //     lightDirection,
      //     PLACEMENT_TYPES.VERTICAL,
      //     angle,
      //   )
      //   break
      // case PLACEMENT_TYPES.PYRAMID:
      //   await renderPyramidPlacement(
      //     canvas,
      //     productImages,
      //     mainShadow,
      //     lightDirection,
      //     PLACEMENT_TYPES.PYRAMID,
      //     angle,
      //   )
      //   break
      // case PLACEMENT_TYPES.COLLAGE1:
      //   await renderCollage1Placement(
      //     canvas,
      //     productImages,
      //     mainShadow,
      //     lightDirection,
      //     PLACEMENT_TYPES.COLLAGE1,
      //     angle,
      //   )
      //   break
      // case PLACEMENT_TYPES.COLLAGE2:
      //   await renderCollage2Placement(
      //     canvas,
      //     productImages,
      //     mainShadow,
      //     lightDirection,
      //     PLACEMENT_TYPES.COLLAGE2,
      //     angle,
      //   )
      //   break
      // case PLACEMENT_TYPES.COLLAGE3:
      //   await renderCollage3Placement(
      //     canvas,
      //     productImages,
      //     mainShadow,
      //     lightDirection,
      //     PLACEMENT_TYPES.COLLAGE3,
      //     angle,
      //   )
      //   break
      case PLACEMENT_TYPES.FLOAT:
        await renderFloatPlacement(
          canvas,
          productImages,
          mainShadow,
          lightDirection,
          PLACEMENT_TYPES.FLOAT,
          angle,
          aspectCounts,
          areAllEdibleProducts || canvasStore.areAllEdibleProducts
        )
        break
      case PLACEMENT_TYPES.CORNER:
        await renderCornerPlacement(
          canvas,
          productImages,
          mainShadow,
          lightDirection,
          PLACEMENT_TYPES.CORNER,
          areAllEdibleProducts || canvasStore.areAllEdibleProducts,
          angle
        )
        break
      case PLACEMENT_TYPES.STAIRCASE:
        await renderStaircaseTriplePlacement(
          canvas,
          productImages,
          0, // Tilt angle set to 0
          lightDirection,
          PLACEMENT_TYPES.STAIRCASE,
          areAllEdibleProducts || canvasStore.areAllEdibleProducts
        )
        break
      case PLACEMENT_TYPES.LAYER:
        await renderLayerPlacement(
          canvas,
          productImages,
          mainShadow,
          lightDirection,
          PLACEMENT_TYPES.LAYER,
          angle,
          areAllEdibleProducts || canvasStore.areAllEdibleProducts,
        )
    }
  }
  canvas.renderAll()
  if (shouldReflow) {
    resetHeadlineSubheadlines(canvas)
    const objects = canvas.getObjects()
    const glowGroup: any = objects.find((obj: any) => obj.layerName.includes('glowGroup'))
    if (glowGroup) {
      reflowTextAfterScaling(canvas, glowGroup)
    } else {
      const sortedProducts = [...productImages].sort((a: any, b: any) => {
        const aLeft = a.getBoundingRect().left
        const bLeft = b.getBoundingRect().left
        return aLeft - bLeft
      })
      reflowTextBasedOnCollisions(canvas, sortedProducts)
    }
  }
}

const resetCanvasImages = (fabricCanvas: Canvas, images: FabricImage[]) => {
  const objects = fabricCanvas.getObjects()

  // Remove individual product objects
  const previousProduct = objects.filter((obj: any) => obj.layerName?.includes('Product'))
  previousProduct?.forEach((product: any) => {
    detachContactShadow(product)
    fabricCanvas.remove(product)
  })

  // Remove previous shadows if exists
  const previousShadow = objects.filter(
    (obj: any) => obj.layerName === 'Shadow' || obj.layerName == layerNames.AIShadow,
  )
  previousShadow?.forEach((shadow: any) => {
    fabricCanvas.remove(shadow)
  })

  // Remove product-related groups
  const productGroups = objects.filter(
    (obj: any) => obj.type === 'group' && !obj.layerName.includes('CTA'),
  )
  productGroups?.forEach((group: any) => fabricCanvas.remove(group))

  // Reset and re-add images
  images.forEach((img) => {
    img.set({
      left: 0,
      top: 0,
      scaleX: 1,
      scaleY: 1,
      angle: 0,
      shadow: null,
    })
  })
}

const extendToObject = () => {
  FabricObject.prototype.toObject = (function (toObject) {
    return function (this: any) {
      return {
        ...toObject.call(this),
        productId: this.productId,
        layerId: this.layerId,
        layerName: this.layerName,
        layerType: this.layerType,
        isMultiple: this.isMultiple,
        autofit: this.autofit,
        maxLines: this.maxLines,
        text: this.text,
        fontSize: this.fontSize,
        fontWeight: this.fontWeight,
        fontFamily: this.fontFamily,
        fontStyle: this.fontStyle,
        lineHeight: this.lineHeight,
        editable: this.editable,
        textAlign: this.textAlign,
        imageFit: this.imageFit,
        positionIndex: this.positionIndex,
        hasOriginChanged: this.hasOriginChanged,
        deltaY: this.deltaY,
        renderAtLevel: this.renderAtLevel,
        aiShadowProperties: this.aiShadowProperties,
        rx: this.rx,
        ry: this.ry,
      }
    }
  })(FabricObject.prototype.toObject)
}
// Single product placements

export async function renderProductForShadowInput(
  img: FabricImage,
  canvasWidth?: number,
  canvasHeight?: number
) {
  const canvasSize = useAdSizeConfigStore().canvasSize
  const width = canvasWidth ?? canvasSize.width + 1
  const height = canvasHeight ?? canvasSize.height + 1
  const canvas = new Canvas(undefined, {
    width: width,
    height: height,
    preserveObjectStacking: true,
    selection: false,
  })

  img.set({
    layerName: layerNames.Product
  })

  canvas.add(img)
  canvas.renderAll()
  try {
    await generateAIShadows(canvas)
  } catch { }
}

async function renderFrontSingle(
  canvas: Canvas,
  img: FabricImage,
  mainShadow: string,
  lighting: string,
  placementType: string,
  angle: number = 0,
) {
  extendToObject()
  const placementArea = getPlacementBounds('front1')
  const areaWidth = placementArea.right - placementArea.left
  const areaHeight = placementArea.bottom - placementArea.top

  const centerX = placementArea.left + areaWidth / 2
  const bottomY = placementArea.bottom

  if ((img as any).isMultiple) {
    angle = 0
  }

  // Scale image to occupy most of the area, keeping margin
  const maxWidth = areaWidth * ((img as any).aspectType == PRODUCT_ASPECT_TYPES.HORIZONTAL ? 0.9 : 0.75)
  const maxHeight = areaHeight * ((img as any).aspectType == PRODUCT_ASPECT_TYPES.VERTICAL ? 0.9 : 0.75)
  const scale = Math.min(maxWidth / img.width, maxHeight / img.height)
  img.scale(scale)
  img.set({
    left: centerX,
    top: bottomY - img.getScaledHeight() / 2,
    originX: 'center',
    originY: 'center',
    angle: angle,
    shadow: null,
    layerName: 'Product1',
  })
  img.setCoords()
  canvas.add(img)

  let isAiShadowApplied = false
  if (angle == 0) {
    try {
      await generateAIShadows(canvas)
      isAiShadowApplied = true
    } catch { }
  }

  if (lighting != DIRECTIONS.NONE) {
    if (angle != 0 || !isAiShadowApplied) {
      if ((img as any).isMultiple) {
        img.shadow = createDropShadow(img, DIRECTIONS.BOTTOM)
      } else {
        // Shadow
        const shadowType = 'rotate'

        const shadowImage = await createContactShadow(img, shadowType, canvas, angle)
        shadowImage.set('globalCompositeOperation', 'multiply')
        let offsetY = 0
        if ((shadowImage as FabricImage & { layerType?: string }).layerType == 'ellipse') {
          offsetY = shadowImage.getScaledHeight() / 2
        }
        const shape = getBottomShapeLabel(img)
        const { leftBottom, rightBottom, centerBottom } = getBottomCanvasPoints(img, shape)
        let bottomX, bottomY
        if (angle < 0) {
          bottomX = leftBottom.x + shadowImage.getScaledWidth() / 2
          bottomY = leftBottom.y
          shadowImage.left = bottomX
        } else if (angle > 0) {
          bottomX = rightBottom.x - shadowImage.getScaledWidth() / 2
          bottomY = rightBottom.y
          shadowImage.left = bottomX
        } else {
          if (shape == 'pouch') {
            bottomY = leftBottom.y + shadowImage.getScaledHeight() / 2
          } else if (shape == 'wavy') {
            bottomY =
              centerBottom.y +
              shadowImage.getScaledHeight() / 2 -
              offsetY -
              getBottomWaveCurvePoint(img)
          }
          else if (shape == 'tilted') {
            bottomY = rightBottom.y + shadowImage.getScaledHeight() * 0.2
          } else {
            bottomY = centerBottom.y + shadowImage.getScaledHeight() / 2 - offsetY
          }
        }
        shadowImage.set({
          top: bottomY,
        })
        canvas.add(shadowImage)
        const productIndex = canvas.getObjects().indexOf(img)
        canvas.moveObjectTo(shadowImage, productIndex)
      }
    }
  }
  const canvasStore = useCanvasStore()
  canvasStore.setHideLoader(true)
}

function renderTopSingle(
  canvas: Canvas,
  img: FabricImage,
  mainShadow: string,
  lighting: string,
  placementType: string,
  angle: number = 0,
) {
  const placementArea = getPlacementBounds('top1')
  const areaWidth = placementArea.right - placementArea.left
  const areaHeight = placementArea.bottom - placementArea.top

  const centerX = placementArea.left + areaWidth / 2

  const scale = Math.min((areaWidth * 0.9) / img.width, (areaHeight * 0.9) / img.height)
  img.scale(scale)

  const shadow = createShadow(img, placementType, lighting)

  img.set({
    left: centerX,
    top: placementArea.bottom - img.getScaledHeight() / 2,
    originX: 'center',
    originY: 'center',
    angle: angle,
    shadow: shadow,
    layerName: 'Product1',
  })

  canvas.add(img)
  const canvasStore = useCanvasStore()
  canvasStore.setHideLoader(true)
}

function renderFloatSingle(
  canvas: Canvas,
  img: FabricImage,
  mainShadow: string,
  lighting: string,
  placementType: string,
  angle: number = 0,
) {
  const placementArea = getPlacementBounds('default')
  const areaWidth = placementArea.right - placementArea.left
  const areaHeight = placementArea.bottom - placementArea.top
  const centerX = placementArea.left + areaWidth / 2
  const centerY = placementArea.top + areaHeight / 2

  const scale = Math.min((areaWidth * 0.9) / img.width, (areaHeight * 0.9) / img.height)
  img.scale(scale)

  const shadow = createShadow(img, placementType, lighting)

  img.set({
    left: centerX,
    top: centerY,
    originX: 'center',
    originY: 'center',
    angle: angle,
    shadow: shadow,
    layerName: 'Product1',
  })

  canvas.add(img)
  const canvasStore = useCanvasStore()
  canvasStore.setHideLoader(true)
}

function renderCornerSingle(
  canvas: Canvas,
  img: FabricImage,
  mainShadow: string,
  lighting: string,
  placementType: string,
  angle: number = 0,
) {
  const placementArea = getPlacementBounds("corner1")

  const areaLeft = placementArea.left
  const areaTop = placementArea.top
  const areaRight = placementArea.right
  const areaBottom = placementArea.bottom
  const areaWidth = areaRight - areaLeft
  const areaHeight = areaBottom - areaTop

  const angleRad = (angle * Math.PI) / 180

  const abscos = Math.abs(Math.cos(angleRad))
  const abssin = Math.abs(Math.sin(angleRad))
  const scale = Math.min(
    areaWidth / (img.width * abscos + img.height * abssin),
    areaHeight / (img.width * abssin + img.height * abscos),
  )
  img.scale(scale)

  const scaledWidth = img.getScaledWidth()
  const scaledHeight = img.getScaledHeight()

  // Rotated bbox half-dimensions (how far the bbox extends from the center after rotation)
  const bboxHalfW = (scaledWidth * abscos + scaledHeight * abssin) / 2
  const bboxHalfH = (scaledWidth * abssin + scaledHeight * abscos) / 2
  const displacement = 0.20 * scaledWidth
  const imgLeft = areaRight - bboxHalfW + displacement
  const imgTop = areaBottom - bboxHalfH + displacement

  const shadow = createShadow(img, placementType, lighting)

  // Align bottom-center of image to bottom-right corner
  img.set({
    left: imgLeft,
    top: imgTop,
    originX: 'center',
    originY: 'center',
    angle: angle,
    shadow: shadow,
    layerName: 'Product1',
  })

  canvas.add(img)
  const canvasStore = useCanvasStore()
  canvasStore.setHideLoader(true)
}

// Three product placements

async function renderFrontPlacement(
  canvas: Canvas,
  images: FabricImage[],
  mainShadow: string,
  lighting: string,
  placementType: string,
  angle: number = 0,
  aspectCounts: AspectCounts | null = null,
  areAllEdibleProducts: boolean = false
) {
  if (images.length === 0) return
  const frontPlacementFabricImages = []
  const specialPlacement = aspectCounts && (aspectCounts.Vertical + aspectCounts.BoxVertical === 3) ? true : false
  const isOverlapNeeded = aspectCounts && aspectCounts.Vertical === 3 ? false : true
  const placementArea = getPlacementBounds('front3');
  if (areAllEdibleProducts) {
    // Adjust the only left bound to make sure it doesnot touch the cta button
    placementArea.left = getPlacementBounds('maxProductFood').left
  }
  angle = images.some((img: any) => img.isMultiple) ? 0 : angle

  const areaLeft = placementArea.left
  const areaTop = placementArea.top
  const areaRight = placementArea.right
  const areaBottom = placementArea.bottom
  const fixedAngle = Math.abs(angle)
  const areaWidth = areaRight - areaLeft
  const areaHeight = areaBottom - areaTop
  const baseRight = areaRight
  const baseBottom = areaBottom

  const img2 = images.reduce((tallest, current) =>
    current.height / current.width > tallest.height / tallest.width ? current : tallest,
  )
  const secondaryImgs = images.filter((img) => img !== img2)
  const img3 = secondaryImgs.reduce((smallest, current) =>
    current.width / current.height > smallest.width / smallest.height ? current : smallest,
  )
  const finalImage = secondaryImgs.filter((img) => img !== img3)
  const [img1] = finalImage
  const aspect1 = img1.width / img1.height
  const aspect2 = img2.width / img2.height
  const aspect3 = img3.width / img3.height

  const angleRad = (fixedAngle * Math.PI) / 180
  const abscos = Math.abs(Math.cos(angleRad))
  const abssin = Math.abs(Math.sin(angleRad))

  const { share1, share2, share3 } = getWidthSplitWithRotationPenaltyFor3Products(aspect1, aspect2, aspect3, angleRad, 0, -angleRad, 0.3, 0.4);
  const image2HeightFraction = 1
  const heightFraction = 0.8

  let finalScale1;
  const img1WidthScale = (areaWidth * share1) / (img1.width * abscos + img1.height * abssin)
  const img1HeightScale = (areaHeight * heightFraction) / (img1.width * abssin + img1.height * abscos)
  if (img1WidthScale < img1HeightScale) {
    // Intentional scale down to make middle image taller
    finalScale1 = specialPlacement ? img1WidthScale * 0.95 : img1WidthScale
  } else {
    finalScale1 = img1HeightScale
  }
  // img2 is always upright (angle = 0), so cos = 1, sin = 0 — plain dimensions apply
  const finalScale2 = Math.min(
    (areaWidth * share2) / img2.width,
    (areaHeight * image2HeightFraction) / img2.height,
  )
  let finalScale3;
  const img3WidthScale = (areaWidth * share3) / (img3.width * abscos + img3.height * abssin)
  const img3HeightScale = (areaHeight * heightFraction) / (img3.width * abssin + img3.height * abscos)
  if (img3WidthScale < img3HeightScale) {
    // Intentional scale down to make middle image taller
    finalScale3 = specialPlacement ? img3WidthScale * 0.95 : img3WidthScale
  } else {
    finalScale3 = img3HeightScale
  }
  img1.scale(finalScale1)
  img2.scale(finalScale2)
  img3.scale(finalScale3)

  // Rotated axis-aligned bounding box of each scaled image
  let rotBBoxW1 = img1.getScaledWidth() * abscos + img1.getScaledHeight() * abssin
  let rotBBoxH1 = img1.getScaledWidth() * abssin + img1.getScaledHeight() * abscos
  // img2 is upright: its axis-aligned bounding box equals its plain scaled size
  let rotBBoxW2 = img2.getScaledWidth()
  let rotBBoxH2 = img2.getScaledHeight()
  let rotBBoxW3 = img3.getScaledWidth() * abscos + img3.getScaledHeight() * abssin
  let rotBBoxH3 = img3.getScaledWidth() * abssin + img3.getScaledHeight() * abscos

  const overlapPercentage = getRandomNumber(20, 0, 15)

  let overlapPx12, overlapPx23;
  if (!isOverlapNeeded) {
    overlapPx12 = -10
    overlapPx23 = -10
  } else {
    // Pair 1↔2: only img1 is tilted
    const slantComp12 = img1.getScaledHeight() * abssin
    const visOverlap12 = Math.min(img1.getScaledWidth(), img2.getScaledWidth()) * (overlapPercentage / 100)
    overlapPx12 = slantComp12 + visOverlap12

    // Pair 2↔3: only img3 is tilted
    const slantComp23 = img3.getScaledHeight() * abssin
    const visOverlap23 = Math.min(img2.getScaledWidth(), img3.getScaledWidth()) * (overlapPercentage / 100)
    overlapPx23 = slantComp23 + visOverlap23
  }

  // Total span of the three rotated bounding boxes with both overlaps applied
  const currentSpan = rotBBoxW1 + rotBBoxW2 + rotBBoxW3 - overlapPx12 - overlapPx23

  // Ideal boost to make span === areaWidth
  let idealBoost;
  if (!isOverlapNeeded) {
    const rawW = rotBBoxW1 + rotBBoxW2 + rotBBoxW3;
    idealBoost = rawW > 0 ? (areaWidth + overlapPx12 + overlapPx23) / rawW : 1.0;
  } else {
    idealBoost = currentSpan > 0 ? areaWidth / currentSpan : 1.0;
  }

  // Cap so that no product exceeds the height budget
  const maxBoostH = Math.min(
    (areaHeight * heightFraction) / img1.getScaledHeight(),
    (areaHeight * image2HeightFraction) / img2.getScaledHeight(),
    (areaHeight * heightFraction) / img3.getScaledHeight(),
  )
  const overlapBoost = Math.min(idealBoost, maxBoostH)

  // Apply boost
  img1.scale(finalScale1 * overlapBoost)
  img2.scale(finalScale2 * overlapBoost)
  img3.scale(finalScale3 * overlapBoost)

  // Recompute rotated bounding boxes with boosted scale
  rotBBoxW1 = img1.getScaledWidth() * abscos + img1.getScaledHeight() * abssin
  rotBBoxH1 = img1.getScaledWidth() * abssin + img1.getScaledHeight() * abscos
  // img2 remains upright after boost
  rotBBoxW2 = img2.getScaledWidth()
  rotBBoxH2 = img2.getScaledHeight()
  rotBBoxW3 = img3.getScaledWidth() * abscos + img3.getScaledHeight() * abssin
  rotBBoxH3 = img3.getScaledWidth() * abssin + img3.getScaledHeight() * abscos

  // Recompute actual overlap pixels with the boosted scale (per-angle slant)
  let boostedOverlapPx12, boostedOverlapPx23;
  if (!isOverlapNeeded) {
    boostedOverlapPx12 = overlapPx12; // Exactly -10
    boostedOverlapPx23 = overlapPx23; // Exactly -10
  } else {
    const boostedSlantComp12 = img1.getScaledHeight() * abssin   // only img1 is tilted at this boundary
    const boostedVisOverlap12 = Math.min(img1.getScaledWidth(), img2.getScaledWidth()) * (overlapPercentage / 100)
    boostedOverlapPx12 = boostedSlantComp12 + boostedVisOverlap12

    const boostedSlantComp23 = img3.getScaledHeight() * abssin   // only img3 is tilted at this boundary
    const boostedVisOverlap23 = Math.min(img2.getScaledWidth(), img3.getScaledWidth()) * (overlapPercentage / 100)
    boostedOverlapPx23 = boostedSlantComp23 + boostedVisOverlap23
  }

  // --- Positioning (right-to-left: img1 rightmost, img3 leftmost) ---
  // img1: right edge of its rotBBox flush with areaRight
  const img1OffsetY = specialPlacement ? 20 : 0
  const img1CenterX = baseRight - rotBBoxW1 / 2
  const img1CenterY = baseBottom - rotBBoxH1 / 2 - img1OffsetY

  // img2: placed to the left of img1 with overlap
  const img2CenterX = img1CenterX - rotBBoxW1 / 2 - rotBBoxW2 / 2 + boostedOverlapPx12
  const img2CenterY = baseBottom - rotBBoxH2 / 2

  // img3: placed to the left of img2 with overlap
  const img3OffsetY = specialPlacement ? 20 : 0
  const img3CenterX = img2CenterX - rotBBoxW2 / 2 - rotBBoxW3 / 2 + boostedOverlapPx23
  const img3CenterY = baseBottom - rotBBoxH3 / 2 - img3OffsetY

  // Determine shadow type for front placement
  const aspectRatio = (img2.height * img2.scaleY) / (img2.width * img2.scaleX)
  let shadowType
  if (Math.abs(angle % 360) > 20 && Math.abs(angle % 360) < 340) {
    shadowType = 'rotate'
  } else {
    shadowType = aspectRatio > 1.5 ? 'ellipse' : 'square'
  }

  // Set up img1 (Product1)
  img1.set({
    left: img1CenterX,
    top: img1CenterY,
    angle: fixedAngle,
    originX: 'center',
    originY: 'center',
    opacity: 1,
    layerName: 'Product1',
  })

  img1.set('layerId', uuidv4())
  img1.setCoords()
  canvas.add(img1)
  frontPlacementFabricImages.push(img1)

  // Set up img2 (Product2)
  img2.set({
    left: img2CenterX,
    top: img2CenterY,
    angle: 0,
    originX: 'center',
    originY: 'center',
    opacity: 1,
    layerName: 'Product2',
  })

  img2.set('layerId', uuidv4())
  img2.setCoords()
  canvas.add(img2)
  frontPlacementFabricImages.push(img2)

  // Set up img3 (Product3)
  img3.set({
    left: img3CenterX,
    top: img3CenterY,
    angle: -fixedAngle,
    originX: 'center',
    originY: 'center',
    opacity: 1,
    layerName: 'Product3',
  })

  img3.set('layerId', uuidv4())
  img3.setCoords()
  canvas.add(img3)
  frontPlacementFabricImages.push(img3)

  let isAiShadowApplied = false
  let isAiShadowAppliedForMiddleProduct = false
  if (fixedAngle == 0) {
    try {
      await generateAIShadows(canvas)
      isAiShadowApplied = true
    } catch { }
  } else {
    try {
      await generateAIShadows(canvas, [img2])
      isAiShadowAppliedForMiddleProduct = true
    } catch { }
  }

  // Handle shadows for img1
  if (lighting != DIRECTIONS.NONE) {
    if (fixedAngle != 0 || !isAiShadowApplied) {
      if ((img1 as any).isMultiple) {
        img1.shadow = createDropShadow(img1, DIRECTIONS.BOTTOM)
      } else {
        const shadowImage = await createContactShadow(img1, shadowType, canvas, angle)
        const shape = getBottomShapeLabel(img1)
        const { leftBottom, rightBottom, centerBottom } = getBottomCanvasPoints(img1, shape)
        shadowImage.set('globalCompositeOperation', 'multiply')
        shadowImage.set('layerId', (img1 as FabricImage & { layerId?: string }).layerId)

        let offsetY = 0
        if ((shadowImage as FabricImage & { layerType?: string }).layerType == 'ellipse') {
          offsetY = shadowImage.getScaledHeight() / 2
        }

        let bottomY
        if (angle < 0) {
          shadowImage.left = leftBottom.x + shadowImage.getScaledWidth() / 2
          bottomY = leftBottom.y
        } else if (angle > 0) {
          shadowImage.left = rightBottom.x - shadowImage.getScaledWidth() / 2
          bottomY = rightBottom.y
        } else {
          if (shape == 'pouch') {
            bottomY = leftBottom.y + shadowImage.getScaledHeight() / 2
          } else if (shape == 'wavy') {
            bottomY =
              centerBottom.y +
              shadowImage.getScaledHeight() / 2 -
              offsetY -
              getBottomWaveCurvePoint(img1)
          }
          else if (shape == 'tilted') {
            bottomY = rightBottom.y + shadowImage.getScaledHeight() * 0.2
          } else {
            bottomY = centerBottom.y + shadowImage.getScaledHeight() / 2 - offsetY
          }
        }

        shadowImage.set({ top: bottomY })
        canvas.add(shadowImage)
        canvas.moveObjectTo(shadowImage, canvas.getObjects().indexOf(img1))
      }
    }
  }

  // Handle shadows for img2
  if (lighting != DIRECTIONS.NONE) {
    if (!isAiShadowApplied && !isAiShadowAppliedForMiddleProduct) {
      if ((img2 as any).isMultiple) {
        img2.shadow = createDropShadow(img2, DIRECTIONS.BOTTOM)
      } else {
        const shadowImage = await createContactShadow(img2, shadowType, canvas, angle)
        const shape = getBottomShapeLabel(img2)
        const { leftBottom, rightBottom, centerBottom } = getBottomCanvasPoints(img2, shape)
        shadowImage.set('globalCompositeOperation', 'multiply')
        shadowImage.set('layerId', (img2 as FabricImage & { layerId?: string }).layerId)

        let offsetY = 0
        if ((shadowImage as FabricImage & { layerType?: string }).layerType == 'ellipse') {
          offsetY = shadowImage.getScaledHeight() / 2
        }

        let bottomY
        if (angle < 0) {
          shadowImage.left = leftBottom.x + shadowImage.getScaledWidth() / 2
          bottomY = leftBottom.y
        } else if (angle > 0) {
          shadowImage.left = rightBottom.x - shadowImage.getScaledWidth() / 2
          bottomY = rightBottom.y
        } else if (shape == 'wavy') {
          bottomY =
            centerBottom.y +
            shadowImage.getScaledHeight() / 2 -
            offsetY -
            getBottomWaveCurvePoint(img2)
        } else {
          if (shape == 'pouch') {
            bottomY = leftBottom.y + shadowImage.getScaledHeight() / 2
          }
          else if (shape == 'tilted') {
            bottomY = rightBottom.y + shadowImage.getScaledHeight() * 0.2
          }
          else {
            bottomY = centerBottom.y + shadowImage.getScaledHeight() / 2 - offsetY
          }
        }

        shadowImage.set({ top: bottomY })
        canvas.add(shadowImage)
        canvas.moveObjectTo(shadowImage, canvas.getObjects().indexOf(img2))
      }
    }
  }

  // Handle shadows for img3
  if (lighting != DIRECTIONS.NONE) {
    if (fixedAngle != 0 || !isAiShadowApplied) {
      if ((img3 as any).isMultiple) {
        img3.shadow = createDropShadow(img3, DIRECTIONS.BOTTOM)
      } else {
        const shadowImage = await createContactShadow(img3, shadowType, canvas, angle)
        const shape = getBottomShapeLabel(img3)
        const { leftBottom, rightBottom, centerBottom } = getBottomCanvasPoints(img3, shape)
        shadowImage.set('globalCompositeOperation', 'multiply')
        shadowImage.set('layerId', (img3 as FabricImage & { layerId?: string }).layerId)

        let offsetY = 0
        if ((shadowImage as FabricImage & { layerType?: string }).layerType == 'ellipse') {
          offsetY = shadowImage.getScaledHeight() / 2
        }

        let bottomY
        if (angle < 0) {
          shadowImage.left = leftBottom.x + shadowImage.getScaledWidth() / 2
          bottomY = leftBottom.y
        } else if (angle > 0) {
          shadowImage.left = rightBottom.x - shadowImage.getScaledWidth() / 2
          bottomY = rightBottom.y
        } else if (shape == 'wavy') {
          bottomY =
            centerBottom.y +
            shadowImage.getScaledHeight() / 2 -
            offsetY -
            getBottomWaveCurvePoint(img3)
        } else {
          if (shape == 'pouch') {
            bottomY = leftBottom.y + shadowImage.getScaledHeight() / 2
          }
          else if (shape == 'tilted') {
            bottomY = rightBottom.y + shadowImage.getScaledHeight() * 0.2
          }
          else {
            bottomY = centerBottom.y + shadowImage.getScaledHeight() / 2 - offsetY
          }
        }

        shadowImage.set({ top: bottomY })
        canvas.add(shadowImage)
        canvas.moveObjectTo(shadowImage, canvas.getObjects().indexOf(img3))
      }
    }
  }

  // Final positioning and layering
  img3.setCoords()
  canvas.bringObjectToFront(img2)

  // No glow effect for front placement - only individual shadows handled above

  canvas.renderAll()
  const canvasStore = useCanvasStore()
  canvasStore.setHideLoader(true)
}

// Helper: scale image to fit within a bounding box while preserving aspect ratio
function scaleImageWithin(img: FabricImage, maxWidth: number, maxHeight: number) {
  const scale = Math.min(maxWidth / img.width, maxHeight / img.height)
  img.scale(scale)
}

function renderTopPlacement(
  canvas: Canvas,
  images: FabricImage[],
  mainShadow: string,
  lighting: string,
  placementType: string,
  angle: number = 0,
  areAllEdibleProducts: boolean = false,
) {
  const placementArea = areAllEdibleProducts ? getPlacementBounds('maxProductFood') : getPlacementBounds('top3')
  const areaLeft = placementArea.left
  const areaTop = placementArea.top
  const areaRight = placementArea.right
  const areaBottom = placementArea.bottom
  const fixedAngle = Math.abs(angle)
  const areaWidth = areaRight - areaLeft
  const areaHeight = areaBottom - areaTop
  const baseRight = areaRight
  const baseBottom = areaBottom

  const img1 = images[0]
  const img2 = images[1]
  const img3 = images[2]
  const aspect1 = img1.width / img1.height
  const aspect2 = img2.width / img2.height
  const aspect3 = img3.width / img3.height

  const canOverlap = images.filter((img: any) => img.aspectType != PRODUCT_ASPECT_TYPES.VERTICAL).length >= 2
  let gapFraction = 0, overlapPercentage = 0, gapPx = 0
  if (canOverlap) {
    overlapPercentage = 25
  } else {
    gapFraction = 0.015
    gapPx = areaWidth * gapFraction
  }
  const { share1, share2, share3 } = getWidthSplitWithRotationPenaltyFor3Products(aspect1, aspect2, aspect3, 0, 0, 0, 0.3, 0.4);

  const angleRad = (fixedAngle * Math.PI) / 180
  const abscos = Math.abs(Math.cos(angleRad))
  const abssin = Math.abs(Math.sin(angleRad))
  const avilableWidthForProducts = areaWidth - 2 * gapPx


  const finalScale1 = Math.min(
    (avilableWidthForProducts * share1) / (img1.width * abscos + img1.height * abssin),
    areaHeight / (img1.width * abssin + img1.height * abscos),
  )
  const finalScale2 = Math.min(
    (avilableWidthForProducts * share2) / (img2.width * abscos + img2.height * abssin),
    areaHeight / (img2.width * abssin + img2.height * abscos),
  )
  const finalScale3 = Math.min(
    (avilableWidthForProducts * share3) / (img3.width * abscos + img3.height * abssin),
    areaHeight / (img3.width * abssin + img3.height * abscos),
  )
  img1.scale(finalScale1)
  img2.scale(finalScale2)
  img3.scale(finalScale3)

  // Rotated axis-aligned bounding box of each scaled image
  let rotBBoxW1 = img1.getScaledWidth() * abscos + img1.getScaledHeight() * abssin
  let rotBBoxH1 = img1.getScaledWidth() * abssin + img1.getScaledHeight() * abscos
  let rotBBoxW2 = img2.getScaledWidth() * abscos + img2.getScaledHeight() * abssin
  let rotBBoxH2 = img2.getScaledWidth() * abssin + img2.getScaledHeight() * abscos
  let rotBBoxW3 = img3.getScaledWidth() * abscos + img3.getScaledHeight() * abssin
  let rotBBoxH3 = img3.getScaledWidth() * abssin + img3.getScaledHeight() * abscos

  let img1Left, img2Left, img3Left;

  if (canOverlap) {
    const slantComp12 = Math.min(img1.getScaledHeight(), img2.getScaledHeight()) * abssin
    const visOverlap12 = Math.min(img1.getScaledWidth(), img2.getScaledWidth()) * (overlapPercentage / 100)
    const overlapPx12 = slantComp12 + visOverlap12

    const slantComp23 = Math.min(img2.getScaledHeight(), img3.getScaledHeight()) * abssin
    const visOverlap23 = Math.min(img2.getScaledWidth(), img3.getScaledWidth()) * (overlapPercentage / 100)
    const overlapPx23 = slantComp23 + visOverlap23

    // Total span of the three rotated bounding boxes with both overlaps applied
    const currentSpan = rotBBoxW1 + rotBBoxW2 + rotBBoxW3 - overlapPx12 - overlapPx23

    // Ideal boost to make span === areaWidth
    const idealBoost = currentSpan > 0 ? avilableWidthForProducts / currentSpan : 1.0

    // Cap so that no product exceeds the height budget
    const maxBoostH = Math.min(
      areaHeight / img1.getScaledHeight(),
      areaHeight / img2.getScaledHeight(),
      areaHeight / img3.getScaledHeight(),
    )
    const overlapBoost = Math.min(idealBoost, maxBoostH)

    // Apply boost
    img1.scale(finalScale1 * overlapBoost)
    img2.scale(finalScale2 * overlapBoost)
    img3.scale(finalScale3 * overlapBoost)

    // Recompute rotated bounding boxes with boosted scale
    rotBBoxW1 = img1.getScaledWidth() * abscos + img1.getScaledHeight() * abssin
    rotBBoxH1 = img1.getScaledWidth() * abssin + img1.getScaledHeight() * abscos
    rotBBoxW2 = img2.getScaledWidth() * abscos + img2.getScaledHeight() * abssin
    rotBBoxH2 = img2.getScaledWidth() * abssin + img2.getScaledHeight() * abscos
    rotBBoxW3 = img3.getScaledWidth() * abscos + img3.getScaledHeight() * abssin
    rotBBoxH3 = img3.getScaledWidth() * abssin + img3.getScaledHeight() * abscos

    // Recompute actual overlap pixels with the boosted scale (per-angle slant)
    const boostedSlantComp12 = Math.min(img1.getScaledHeight(), img2.getScaledHeight()) * abssin
    const boostedVisOverlap12 = Math.min(img1.getScaledWidth(), img2.getScaledWidth()) * (overlapPercentage / 100)
    const boostedOverlapPx12 = boostedSlantComp12 + boostedVisOverlap12

    const boostedSlantComp23 = Math.min(img2.getScaledHeight(), img3.getScaledHeight()) * abssin
    const boostedVisOverlap23 = Math.min(img2.getScaledWidth(), img3.getScaledWidth()) * (overlapPercentage / 100)
    const boostedOverlapPx23 = boostedSlantComp23 + boostedVisOverlap23

    img1Left = baseRight - rotBBoxW1 / 2
    img2Left = img1Left - rotBBoxW1 / 2 - rotBBoxW2 / 2 + boostedOverlapPx12
    img3Left = img2Left - rotBBoxW2 / 2 - rotBBoxW3 / 2 + boostedOverlapPx23
  } else {
    img1Left = baseRight - rotBBoxW1 / 2
    img2Left = img1Left - rotBBoxW1 / 2 - rotBBoxW2 / 2 - gapPx
    img3Left = img2Left - rotBBoxW2 / 2 - rotBBoxW3 / 2 - gapPx
  }
  const img1Top = baseBottom - rotBBoxH1 / 2
  const img2Top = baseBottom - rotBBoxH2 / 2
  const img3Top = baseBottom - rotBBoxH3 / 2

  img1.set({
    left: img1Left,
    top: img1Top,
    angle: fixedAngle,
    originX: 'center',
    originY: 'center',
    opacity: 1,
    layerName: 'Product1',
    shadow: createShadow(img1, placementType, lighting)
  })

  img2.set({
    left: img2Left,
    top: img2Top,
    angle: fixedAngle,
    originX: 'center',
    originY: 'center',
    opacity: 1,
    layerName: 'Product2',
    shadow: createShadow(img2, placementType, lighting)
  })

  img3.set({
    left: img3Left,
    top: img3Top,
    angle: fixedAngle,
    originX: 'center',
    originY: 'center',
    opacity: 1,
    layerName: 'Product3',
    shadow: createShadow(img3, placementType, lighting)
  })

  img1.set('layerId', uuidv4())
  canvas.add(img1)

  img3.set('layerId', uuidv4())
  canvas.add(img3)

  img2.set('layerId', uuidv4())
  canvas.add(img2)

  canvas.bringObjectToFront(img2)
  canvas.bringObjectToFront(img1)
  canvas.renderAll()

  const canvasStore = useCanvasStore()
  canvasStore.setHideLoader(true)
}

async function renderPyramidPlacement(
  canvas: Canvas,
  images: FabricImage[],
  mainShadow: string,
  lighting: string,
  placementType: string,
  angle: number = 0,
) {
  const sidePadding = 20
  const bottomPadding = 40

  const paddedLeft = PLACEMENT_AREA.left
  const paddedRight = PLACEMENT_AREA.right - sidePadding
  const paddedBottom = PLACEMENT_AREA.bottom - bottomPadding

  const centerX = (paddedLeft + paddedRight) / 2
  const maxWidth = paddedRight - paddedLeft
  const maxHeight = paddedBottom - PLACEMENT_AREA.top
  const fixedAngle = angle

  // Set same aspect ratio images in the bottom
  const aspectRatios = images.map((img) => img.width / img.height)

  const pairs = [
    { pair: [0, 1], diff: Math.abs(aspectRatios[0] - aspectRatios[1]) },
    { pair: [0, 2], diff: Math.abs(aspectRatios[0] - aspectRatios[2]) },
    { pair: [1, 2], diff: Math.abs(aspectRatios[1] - aspectRatios[2]) },
  ]

  // Find the pair with the smallest aspect ratio difference
  const bestPair = pairs.reduce((prev, curr) => (curr.diff < prev.diff ? curr : prev))
  const [i1, i2] = bestPair.pair
  const i3 = [0, 1, 2].find((i) => i !== i1 && i !== i2)!

  // Reorder: bottom row first, top last
  images = [images[i1], images[i2], images[i3]]

  // Compute rows needed (triangle numbers: 1, 2, 3...)
  let rows = 0,
    total = 0
  while (total < images.length) {
    rows++
    total += rows
  }

  let imgIndex = 0
  const rowImages: FabricImage[][] = []

  // Build rows from bottom to top
  for (let r = rows; r > 0; r--) {
    const count = Math.min(r, images.length - imgIndex)
    const row: FabricImage[] = []

    const maxImgWidth = (maxWidth - 10 * (count - 1)) / count
    const maxImgHeight = maxHeight / rows

    const rowPositionFromBottom = rows - r
    const scaleFactor = 1 - 0.1 * rowPositionFromBottom

    for (let i = 0; i < count && imgIndex < images.length; i++, imgIndex++) {
      const img = images[imgIndex]
      const baseScale = Math.min(maxImgWidth / img.width, maxImgHeight / img.height)
      img.scale(baseScale * scaleFactor)
      row.push(img)
    }

    rowImages.push(row)
  }

  // === Normalize bottom row (baseline & height match for 2 products) ===
  const bottomRow = rowImages[0]
  if (bottomRow.length === 2) {
    const [img1, img2] = bottomRow

    const availableHeight = paddedBottom - PLACEMENT_AREA.top
    const availableWidth = paddedRight - paddedLeft

    // Natural dimensions
    const h1 = img1.height
    const h2 = img2.height
    const w1 = img1.width
    const w2 = img2.width

    // Step 1: Uniform target height based on height constraint
    const scale1 = availableHeight / h1
    const scale2 = availableHeight / h2
    let targetHeight = Math.min(h1 * scale1, h2 * scale2)

    // Step 2: Compute total width of both images at target height
    const scaledW1 = (targetHeight / h1) * w1
    const scaledW2 = (targetHeight / h2) * w2
    const spacing = 10
    const totalWidth = scaledW1 + scaledW2 + spacing

    // Step 3: If width exceeds, reduce targetHeight further
    if (totalWidth > availableWidth) {
      const widthScaleFactor = (availableWidth - spacing) / (scaledW1 + scaledW2)
      targetHeight *= widthScaleFactor
    }

    // Step 4: Final scaling
    const unifiedScale1 = targetHeight / h1
    const unifiedScale2 = targetHeight / h2

    img1.scale(unifiedScale1)
    img2.scale(unifiedScale2)
  }

  // === Place images row by row ===
  let currentY = paddedBottom
  let shadowType: string
  for (let i = 0; i < rowImages.length; i++) {
    const row = rowImages[i]
    const tallestImg = Math.max(...row.map((img) => img.getScaledHeight()))
    const totalWidth =
      row.reduce((sum, img) => sum + img.getScaledWidth(), 0) + 10 * (row.length - 1)

    const startX = centerX - totalWidth / 2
    let x = startX

    currentY -= tallestImg // shift up by tallest image in row

    for (let index = 0; index < row.length; index++) {
      const img = row[index]
      const imgHeight = img.getScaledHeight()

      img.set({
        left: x + img.getScaledWidth() / 2,
        top: currentY + imgHeight / 2,
        originX: 'center',
        originY: 'center',
        layerName: `Product${index + 1}`,
        angle: fixedAngle,
      })

      const aspectRatio = (img.height * img.scaleY) / (img.width * img.scaleX)

      if (Math.abs(angle % 360) > 20 && Math.abs(angle % 360) < 340) {
        shadowType = 'rotate'
      } else {
        shadowType = aspectRatio > 1.5 ? 'ellipse' : 'square'
      }

      img.set('layerId', uuidv4())
      if (lighting != DIRECTIONS.NONE) {
        if (images.some((img: any) => img.isMultiple)) {
          img.shadow = createDropShadow(img, DIRECTIONS.BOTTOM)
        } else {
          const bottomPoint = getBottomPoint(img, fixedAngle)
          const shadowImage = await createContactShadow(img, shadowType, canvas, fixedAngle)
          shadowImage.set('globalCompositeOperation', 'multiply')
          shadowImage.set('layerId', (img as FabricImage & { layerId?: string }).layerId)
          let centerShadowLeft = bottomPoint.x + shadowImage.getScaledWidth() / 2
          let offset = 0
          if (!(fixedAngle === 0 && shadowType === 'square')) {
            offset = shadowImage.getScaledHeight() / 2
          }
          if (fixedAngle > 0) {
            centerShadowLeft = bottomPoint.x - shadowImage.getScaledWidth() / 2
          }
          shadowImage.set({
            left: centerShadowLeft,
            top: img.top + img.getScaledHeight() / 2 + shadowImage.getScaledHeight() / 2 - offset,
          })
          canvas.add(shadowImage)
        }
      }
      canvas.add(img)
      x += img.getScaledWidth() + 10
    }
  }
  const canvasStore = useCanvasStore()
  canvasStore.setHideLoader(true)
}

async function renderLayerPlacement(
  canvas: Canvas,
  images: FabricImage[],
  mainShadow: string,
  lighting: string,
  placementType: string,
  angle: number = 0,
  areAllEdibleProducts: boolean = false
) {
  // Remove angle for pyramid
  angle = 0
  lighting = DIRECTIONS.FRONT

  const placementArea = areAllEdibleProducts ? getPlacementBounds('maxProductFood') : getPlacementBounds('layer3')
  const areaLeft = placementArea.left
  const areaRight = placementArea.right
  const areaBottom = placementArea.bottom
  const areaTop = placementArea.top
  const areaWidth = areaRight - areaLeft
  const areaHeight = areaBottom - areaTop

  images.sort((a, b) => a.height / a.width - b.height / b.width)
  const img1 = images[0]
  const img2 = images[1]
  const img3 = images[2]

  const img1AspectRatio = img1.height / img1.width
  const img2AspectRatio = img2.height / img2.width
  const img3AspectRatio = img3.height / img3.width

  // This placement is only for 3 products and we place two products in the bottom row and 1 product in the top row

  const { heightShare1, heightShare2 } = getHeightSplitForPairWithRotationPenalty(img2AspectRatio, img3AspectRatio, 0, 0, 0.35, 0.45);
  const topRowHeight = areaHeight * heightShare2;
  const { share1, share2 } = getWidthSplitWithRotationPenaltyFor2Products(img1AspectRatio, img2AspectRatio, 0, 0, 0, 0.4, 0.6)
  const img1Scale = Math.min((areaWidth * share1) / img1.width, (areaHeight * heightShare1) / img1.height)
  const img2Scale = Math.min((areaWidth * share2) / img2.width, (areaHeight * heightShare1) / img2.height)
  img1.scale(img1Scale)
  img2.scale(img2Scale)

  const overlapPercentage = getRandomNumber(30, 0, 5);
  let overlapPx = Math.min(img1.getScaledWidth(), img2.getScaledWidth()) * overlapPercentage / 100

  const totalWidth = img1.getScaledWidth() + img2.getScaledWidth() - overlapPx
  const widthScaleFactor = areaWidth / totalWidth;
  const heightScaleFactor = (areaHeight * heightShare1) / Math.max(img1.getScaledHeight(), img2.getScaledHeight());
  const boostScale = Math.min(widthScaleFactor, heightScaleFactor);
  img1.scale(img1.scaleX * boostScale);
  img2.scale(img2.scaleX * boostScale);
  overlapPx = Math.min(img1.getScaledWidth(), img2.getScaledWidth()) * overlapPercentage / 100

  const img1Left = areaRight - img1.getScaledWidth() / 2
  const img1Top = areaBottom - img1.getScaledHeight() / 2
  const img2Left = img1Left - img1.getScaledWidth() / 2 + overlapPx - img2.getScaledWidth() / 2
  // Img2 is kept 20% above img1
  let img2Top = img1Top - (Math.max(img1.getScaledHeight(), img2.getScaledHeight()) * 0.20)
  // Additional check that it do not come below the placement area
  if (img2Top + img2.getScaledHeight() / 2 > areaBottom) {
    img2Top = areaBottom - img2.getScaledHeight() / 2
  }

  img1.set({
    left: img1Left,
    top: img1Top,
    originX: 'center',
    originY: 'center',
    layerName: `Product1`,
    angle: angle
  })

  img2.set({
    left: img2Left,
    top: img2Top,
    originX: 'center',
    originY: 'center',
    layerName: `Product2`,
    angle: angle
  })

  img1.set('layerId', uuidv4())
  canvas.add(img1)

  img2.set('layerId', uuidv4())
  canvas.add(img2)

  // Code for handling placement for top row
  const baseBottomForTopRow = Math.max(img1.top - img1.getScaledHeight() / 2, img2.top - img2.getScaledHeight() / 2)
  const maxHeightAvilableForTopRow = Math.min(topRowHeight, baseBottomForTopRow - areaTop)
  const bottomRowWidth = img1.getScaledWidth() + img2.getScaledWidth() - overlapPx
  const maxWidthAvilableForTopRow = bottomRowWidth * 0.90
  const img3Scale = Math.min(maxWidthAvilableForTopRow / img3.width, maxHeightAvilableForTopRow / img3.height)
  img3.scale(img3Scale)

  // Calculate actual left-over space for further boost
  // Since img3 will overlap the bottom row by 10%, only 90% of its current scaled height extends upwards
  const currentUpwardHeight = img3.getScaledHeight() * 0.90;
  const heightBoost = maxHeightAvilableForTopRow / currentUpwardHeight;
  const widthBoost = maxWidthAvilableForTopRow / img3.getScaledWidth();
  const img3BoostScale = Math.min(widthBoost, heightBoost);

  img3.scale(img3.scaleX * img3BoostScale);

  const img3Top = baseBottomForTopRow - (img3.getScaledHeight() / 2) + img3.getScaledHeight() * 0.10

  // Keep img3 center aligned horizontally to the bottom row products
  const img3Left = (img2Left - img2.getScaledWidth() / 2 + img1Left + img1.getScaledWidth() / 2) / 2;

  const groupTopRaw = Math.min(
    img1.top - img1.getScaledHeight() / 2,
    img2.top - img2.getScaledHeight() / 2,
    img3Top - img3.getScaledHeight() / 2
  );
  const groupBottomRaw = Math.max(
    img1.top + img1.getScaledHeight() / 2,
    img2.top + img2.getScaledHeight() / 2,
    img3Top + img3.getScaledHeight() / 2
  );
  
  const groupTotalHeight = groupBottomRaw - groupTopRaw;
  const desiredGroupTop = areaTop + (areaHeight - groupTotalHeight) / 2;
  const verticalOffset = desiredGroupTop - groupTopRaw;

  img1.set('top', img1.top + verticalOffset);
  img2.set('top', img2.top + verticalOffset);
  img1.setCoords();
  img2.setCoords();


  img3.set({
    left: img3Left,
    top: img3Top + verticalOffset,
    originX: 'center',
    originY: 'center',
    layerName: `Product3`,
    angle: angle
  });

  img3.set('layerId', uuidv4());
  canvas.add(img3);

  canvas.bringObjectToFront(img3);
  canvas.bringObjectToFront(img2);
  canvas.bringObjectToFront(img1);

  let isAiShadowApplied = false
  if (angle == 0) {
    try {
      await generateAIShadows(canvas)
      isAiShadowApplied = true
    } catch { }
  }

  if (!isAiShadowApplied) {
    let shadowType: string
    for (let i = 0; i < images.length; i++) {
      const img: any = images[i]
      const aspectRatio = (img.height * img.scaleY) / (img.width * img.scaleX)

      if (Math.abs(angle % 360) > 20 && Math.abs(angle % 360) < 340) {
        shadowType = 'rotate'
      } else {
        shadowType = aspectRatio > 1.5 ? 'ellipse' : 'square'
      }

      // Do not include shadow for top product

      if (lighting != DIRECTIONS.NONE) {
        if ((img as any).isMultiple) {
          img.shadow = createDropShadow(img, DIRECTIONS.BOTTOM)
        } else {
          const shadowImage = await createContactShadow(img, shadowType, canvas, angle)
          shadowImage.set('globalCompositeOperation', 'multiply')
          let offsetY = 0
          if ((shadowImage as FabricImage & { layerType?: string }).layerType == 'ellipse') {
            offsetY = shadowImage.getScaledHeight() / 2
          }
          const shape = getBottomShapeLabel(img)
          const { leftBottom, rightBottom, centerBottom } = getBottomCanvasPoints(img, shape)
          let bottomX, bottomY
          if (angle < 0) {
            bottomX = leftBottom.x + shadowImage.getScaledWidth() / 2
            bottomY = leftBottom.y
            shadowImage.left = bottomX
          } else if (angle > 0) {
            bottomX = rightBottom.x - shadowImage.getScaledWidth() / 2
            bottomY = rightBottom.y
            shadowImage.left = bottomX
          } else {
            if (shape == 'pouch') {
              bottomY = leftBottom.y + shadowImage.getScaledHeight() / 2
            } else if (shape == 'wavy') {
              bottomY =
                centerBottom.y +
                shadowImage.getScaledHeight() / 2 -
                offsetY -
                getBottomWaveCurvePoint(img)
            }
            else if (shape == 'tilted') {
              bottomY = rightBottom.y + shadowImage.getScaledHeight() * 0.2
            }
            else {
              bottomY = centerBottom.y + shadowImage.getScaledHeight() / 2 - offsetY
            }
          }
          shadowImage.set({
            top: bottomY,
          })
          canvas.add(shadowImage)
          const productIndex = canvas.getObjects().indexOf(img)
          canvas.moveObjectTo(shadowImage, productIndex)
        }
      }
    }
  }

  // Send the top product to behind the bottom products
  const img: any = images[2]
  // Move shadow above the duo tone
  const duoToneShapes = canvas
    .getObjects()
    .filter(
      (obj: any) =>
        obj.layerName == layerNames.BackgroundShape ||
        obj.layerName == layerNames.BackgroundTexture,
    )

  canvas.moveObjectTo(img, duoToneShapes.length)
  const shadow = canvas
    .getObjects()
    .find((o: any) => o.layerName == 'Shadow' && o.layerId == img.layerId)
  if (shadow) {
    canvas.moveObjectTo(shadow, duoToneShapes.length)
  }

  const canvasStore = useCanvasStore()
  canvasStore.setHideLoader(true)
}

async function renderVerticalPlacement(
  canvas: Canvas,
  images: FabricImage[],
  mainShadow: string,
  lighting: string,
  placementType: string,
  angle: number = 0,
) {
  const spacing = 30
  const areaHeight = PLACEMENT_AREA.bottom - PLACEMENT_AREA.top
  const totalHeight = images.reduce((sum) => sum + 140, 0) + spacing * (images.length - 1)
  let y = PLACEMENT_AREA.top + (areaHeight - totalHeight) / 2
  const fixedAngle = angle
  const centerX = (PLACEMENT_AREA.left + PLACEMENT_AREA.right) / 2
  let shadowType = ''

  if (lighting === DIRECTIONS.BEHIND) {
    images.forEach((img, index) => {
      const scale = 140 / img.height
      img.scale(scale)
      img.set({
        left: centerX,
        top: y + img.getScaledHeight() / 2,
        originX: 'center',
        originY: 'center',
        layerName: `Product${index + 1}`,
        angle: fixedAngle,
        shadow: null,
      })
      y += img.getScaledHeight() + spacing
    })

    // Calculate bounding box accounting for center origins
    const boundingRect = images.reduce(
      (acc, img) => {
        const actualLeft = img.left! - img.getScaledWidth() / 2
        const actualTop = img.top! - img.getScaledHeight() / 2
        return {
          left: Math.min(acc.left, actualLeft),
          top: Math.min(acc.top, actualTop),
          right: Math.max(acc.right, actualLeft + img.getScaledWidth()),
          bottom: Math.max(acc.bottom, actualTop + img.getScaledHeight()),
        }
      },
      {
        left: Infinity,
        top: Infinity,
        right: -Infinity,
        bottom: -Infinity,
      },
    )

    const group = new Group([], {
      left: boundingRect.left,
      top: boundingRect.top,
      subTargetCheck: true,
      interactive: false,
    })

    images.forEach((img: any) => {
      const imgGlobalLeft = img.left! - img.getScaledWidth() / 2
      const imgGlobalTop = img.top! - img.getScaledHeight() / 2

      const relativeLeft = imgGlobalLeft - boundingRect.left
      const relativeTop = imgGlobalTop - boundingRect.top

      const clonedImg = new FabricImage(img.getElement(), {
        left: relativeLeft,
        top: relativeTop,
        scaleX: img.scaleX,
        scaleY: img.scaleY,
        angle: img.angle,
        originX: 'left',
        originY: 'top',
        objectCaching: false,
        layerName: img.layerName,
      })

      canvas.remove(img)
      group.add(clonedImg)
    })

    group.set({
      left: boundingRect.left,
      top: boundingRect.top,
    })
    group.setCoords()
    group.set('shadow', createGlowShadow(group))
    group.set('layerName', 'glowGroup')
    canvas.add(group)
    canvas.bringObjectToFront(group)
    canvas.requestRenderAll()
  } else {
    images.forEach(async (img, index) => {
      const scale = 140 / img.height
      img.scale(scale)
      img.set({
        left: centerX,
        top: y + img.getScaledHeight() / 2,
        originX: 'center',
        originY: 'center',
        layerName: `Product${index + 1}`,
        angle: fixedAngle,
      })

      const aspectRatio = (img.height * img.scaleY) / (img.width * img.scaleX)
      if (!shadowType) {
        if (Math.abs(angle % 360) > 20 && Math.abs(angle % 360) < 340) {
          shadowType = 'rotate'
        } else {
          shadowType = aspectRatio > 1.5 ? 'ellipse' : 'square'
        }
      }
      const offset = aspectRatio > 1.5 ? 20 : 0

      if (lighting === DIRECTIONS.TOP) {
        if (images.some((img: any) => img.isMultiple)) {
          img.shadow = createDropShadow(img, DIRECTIONS.BOTTOM)
        } else {
          img.set('layerId', uuidv4())
          const shadowImage = await createContactShadow(img, shadowType, canvas)
          shadowImage.set('globalCompositeOperation', 'multiply')
          shadowImage.set('layerId', (img as FabricImage & { layerId?: string }).layerId)
          shadowImage.set({
            left: centerX,
            top: y + img.getScaledHeight() + shadowImage.getScaledHeight() / 2 - offset,
          })
          canvas.add(shadowImage)
        }
      } else {
        img.set('shadow', createShadow(img, placementType, lighting))
      }

      canvas.add(img)
      y += img.getScaledHeight() + spacing
    })

    const canvasStore = useCanvasStore()
    canvasStore.setHideLoader(true)
  }
}

function renderCollage1Placement(
  canvas: Canvas,
  images: FabricImage[],
  mainShadow: string,
  lighting: string,
  placementType: string,
  angle: number = 0,
) {
  const areaWidth = PLACEMENT_AREA.right - PLACEMENT_AREA.left
  const areaHeight = PLACEMENT_AREA.bottom - PLACEMENT_AREA.top

  images = reordingImages(images)

  const img1 = images[0]
  let widthRatio = (areaWidth * 0.5) / img1.width
  let heightRatio = (areaHeight * 0.8) / img1.height
  const fixedAngle = angle
  img1.scale(Math.min(widthRatio, heightRatio))
  img1.set({
    left: PLACEMENT_AREA.left + img1.getScaledWidth() * 0.5,
    top: PLACEMENT_AREA.top + areaHeight * 0.5,
    shadow: createShadow(img1, placementType, lighting),
    layerName: 'Product1',
    originX: 'center',
    originY: 'center',
    angle: fixedAngle,
  })
  canvas.add(img1)
  canvas.renderAll()
  // === Two stacked smaller products on the bottom right ===
  const img2 = images[1]
  const img3 = images[2]

  widthRatio = (areaWidth * 0.45) / img2.width
  heightRatio = (areaHeight * 0.45) / img2.height
  img2.scale(Math.min(widthRatio, heightRatio))

  const rightStartX = PLACEMENT_AREA.left
  // First small product (top of stack, slightly higher than before)
  img2.set({
    left: rightStartX + areaWidth * 0.75,
    top: img1.top - img2.getScaledHeight() * 0.5,
    shadow: createShadow(img2, placementType, lighting),
    layerName: 'Product2',
    originX: 'center',
    originY: 'center',
    angle: fixedAngle,
  })
  canvas.add(img2)
  canvas.renderAll()

  // Second small product (bottom of stack)
  widthRatio = (areaWidth * 0.45) / img3.width
  heightRatio = (areaHeight * 0.45) / img3.height
  img3.scale(Math.min(widthRatio, heightRatio))
  let positionTwo = img3.getScaledHeight() > img3.getScaledWidth() ? 0.75 : 0.5
  if (img3.getScaledHeight() > img3.getScaledWidth()) {
    positionTwo = areaHeight * 0.75
  } else {
    positionTwo = areaHeight * 0.5 + img2.getScaledHeight() * 0.5
  }
  img3.set({
    left: rightStartX + areaWidth * 0.75,
    top: img1.top + img3.getScaledHeight() * 0.5 + 16,
    shadow: createShadow(img3, placementType, lighting),
    layerName: 'Product3',
    originX: 'center',
    originY: 'center',
    angle: fixedAngle,
  })

  canvas.add(img3)
  canvas.renderAll()
  const canvasStore = useCanvasStore()
  canvasStore.setHideLoader(true)
}

function renderCollage2Placement(
  canvas: Canvas,
  images: FabricImage[],
  mainShadow: string,
  lighting: string,
  placementType: string,
  angle: number = 0,
) {
  const areaWidth = PLACEMENT_AREA.right - PLACEMENT_AREA.left
  const areaHeight = PLACEMENT_AREA.bottom - PLACEMENT_AREA.top
  // const spacing = areaWidth * 0.1
  images = reordingImages(images)

  const img1 = images[0]
  let widthRatio = (areaWidth * 0.5) / img1.width
  let heightRatio = (areaHeight * 0.8) / img1.height
  const fixedAngle = angle
  img1.scale(Math.min(widthRatio, heightRatio))
  img1.set({
    left: PLACEMENT_AREA.left + areaWidth * 0.75,
    top: PLACEMENT_AREA.top + areaHeight * 0.5,
    shadow: createShadow(img1, placementType, lighting),
    layerName: 'Product1',
    originX: 'center',
    originY: 'center',
    angle: fixedAngle,
  })
  canvas.add(img1)
  canvas.renderAll()
  // === Two stacked smaller products on the bottom right ===
  const img2 = images[1]
  const img3 = images[2]

  widthRatio = (areaWidth * 0.45) / img2.width
  heightRatio = (areaHeight * 0.45) / img2.height
  img2.scale(Math.min(widthRatio, heightRatio))

  const rightStartX = PLACEMENT_AREA.left

  // First small product (top of stack, slightly higher than before)
  img2.set({
    left: rightStartX + areaWidth * 0.25,
    top: img1.top - img2.getScaledHeight() * 0.5,
    shadow: createShadow(img2, placementType, lighting),
    layerName: 'Product2',
    originX: 'center',
    originY: 'center',
    angle: fixedAngle,
  })
  canvas.add(img2)
  canvas.renderAll()

  // Second small product (bottom of stack)
  widthRatio = (areaWidth * 0.45) / img3.width
  heightRatio = (areaHeight * 0.45) / img3.height
  img3.scale(Math.min(widthRatio, heightRatio))

  img3.set({
    left: rightStartX + areaWidth * 0.25,
    top: img1.top + img3.getScaledHeight() * 0.5 + 16,
    shadow: createShadow(img3, placementType, lighting),
    layerName: 'Product3',
    originX: 'center',
    originY: 'center',
    angle: fixedAngle,
  })

  canvas.add(img3)
  canvas.renderAll()
  const canvasStore = useCanvasStore()
  canvasStore.setHideLoader(true)
}

function renderCollage3Placement(
  canvas: Canvas,
  images: FabricImage[],
  mainShadow: string,
  lighting: string,
  placementType: string,
  angle: number = 0,
) {
  const areaWidth = PLACEMENT_AREA.right - PLACEMENT_AREA.left
  const areaHeight = PLACEMENT_AREA.bottom - PLACEMENT_AREA.top
  const fixedAngle = angle
  images = reordingImages(images)
  // Main top image
  const img1 = images[0]
  const topMaxHeight = areaHeight * 0.4
  const scale1 = Math.min((areaWidth * 0.85) / img1.width, topMaxHeight / img1.height)
  img1.scale(scale1)
  img1.set({
    left: PLACEMENT_AREA.left + areaWidth * 0.5,
    top: PLACEMENT_AREA.top + areaHeight * 0.25,
    shadow: createShadow(img1, placementType, lighting),
    layerName: 'Product1',
    originX: 'center',
    originY: 'center',
    angle: fixedAngle,
  })

  // Bottom product images
  const img2 = images[1]
  const img3 = images[2]
  // Increase size of bottom products
  const smallMaxWidth = areaWidth * 0.45
  const smallMaxHeight = areaHeight * 0.45 // increased from 0.25
  const scale2 = Math.min(smallMaxWidth / img2.width, smallMaxHeight / img2.height)
  const scale3 = Math.min(smallMaxWidth / img3.width, smallMaxHeight / img3.height)
  img2.scale(scale2)
  img3.scale(scale3)

  let startX = PLACEMENT_AREA.left + areaWidth * 0.25
  const bottomY = PLACEMENT_AREA.top + areaHeight * 0.5
  img2.set({
    left: startX,
    top: bottomY + img2.getScaledHeight() * 0.5,
    shadow: createShadow(img2, placementType, lighting),
    layerName: 'Product2',
    originX: 'center',
    originY: 'center',
    angle: fixedAngle,
  })
  startX = PLACEMENT_AREA.left + areaWidth * 0.75
  img3.set({
    left: startX,
    top: bottomY + img3.getScaledHeight() * 0.5,
    shadow: createShadow(img3, placementType, lighting),
    layerName: 'Product3',
    originX: 'center',
    originY: 'center',
    angle: fixedAngle,
  })

  canvas.add(img1, img2, img3)
  const canvasStore = useCanvasStore()
  canvasStore.setHideLoader(true)
}

function renderFloatPlacement(
  canvas: Canvas,
  images: FabricImage[],
  mainShadow: string,
  lighting: string,
  placementType: string,
  angle: number = 0,
  aspectCounts: AspectCounts | null = null,
  areAllEdibleProducts: boolean = false
) {
  const placementArea = getPlacementBounds('float3')
  if(areAllEdibleProducts){
    placementArea.left = getPlacementBounds('maxProductFood').left
  }
  const areaLeft = placementArea.left
  const areaTop = placementArea.top
  const areaRight = placementArea.right
  const areaBottom = placementArea.bottom
  const fixedAngle = Math.abs(angle)
  const areaWidth = areaRight - areaLeft
  const areaHeight = areaBottom - areaTop
  const baseRight = areaRight
  const baseBottom = areaBottom
  const areAllProdsVertical = aspectCounts?.Vertical == 3

  const img2 = images.reduce((tallest, current) =>
    current.height / current.width > tallest.height / tallest.width ? current : tallest,
  )
  const secondaryImgs = images.filter((img) => img !== img2)
  const img1 = secondaryImgs[0]
  const img3 = secondaryImgs[1]

  const aspect1 = img1.width / img1.height
  const aspect2 = img2.width / img2.height
  const aspect3 = img3.width / img3.height

  const angleRad = (fixedAngle * Math.PI) / 180
  const abscos = Math.abs(Math.cos(angleRad))
  const abssin = Math.abs(Math.sin(angleRad))

  const overlapPercentage = areAllProdsVertical ? getRandomNumber(10, 0, 5) : getRandomNumber(20, 0, 10)
  const { share1, share2, share3 } = getWidthSplitWithRotationPenaltyFor3Products(aspect1, aspect2, aspect3, angleRad, 0, -angleRad, 0.2, 0.5);
  const image2HeightFraction = 1
  const heightFraction = 1


  const img1WidthScale = (areaWidth * share1) / (img1.width * abscos + img1.height * abssin)
  const img1HeightScale = (areaHeight * heightFraction) / (img1.width * abssin + img1.height * abscos)
  const finalScale1 = Math.min(img1WidthScale, img1HeightScale) * 0.90

  const finalScale2 = Math.min(
    (areaWidth * share2) / img2.width,
    (areaHeight * image2HeightFraction) / img2.height,
  )

  const img3WidthScale = (areaWidth * share3) / (img3.width * abscos + img3.height * abssin)
  const img3HeightScale = (areaHeight * heightFraction) / (img3.width * abssin + img3.height * abscos)
  const finalScale3 = Math.min(img3WidthScale, img3HeightScale) * 0.90

  img1.scale(finalScale1)
  img2.scale(finalScale2)
  img3.scale(finalScale3)

  // Rotated axis-aligned bounding box of each scaled image
  let rotBBoxW1 = img1.getScaledWidth() * abscos + img1.getScaledHeight() * abssin
  let rotBBoxH1 = img1.getScaledWidth() * abssin + img1.getScaledHeight() * abscos
  let rotBBoxW2 = img2.getScaledWidth()
  let rotBBoxH2 = img2.getScaledWidth()
  let rotBBoxW3 = img3.getScaledWidth() * abscos + img3.getScaledHeight() * abssin
  let rotBBoxH3 = img3.getScaledWidth() * abssin + img3.getScaledHeight() * abscos

  const slantComp12 = img1.getScaledHeight() * abssin
  const visOverlap12 = Math.min(img1.getScaledWidth(), img2.getScaledWidth()) * (overlapPercentage / 100)
  const overlapPx12 = slantComp12 + visOverlap12

  // Pair 2↔3: only img3 is tilted
  const slantComp23 = img3.getScaledHeight() * abssin
  const visOverlap23 = Math.min(img2.getScaledWidth(), img3.getScaledWidth()) * (overlapPercentage / 100)
  const overlapPx23 = slantComp23 + visOverlap23

  // Total span of the three rotated bounding boxes with both overlaps applied
  const currentSpan = rotBBoxW1 + rotBBoxW2 + rotBBoxW3 - overlapPx12 - overlapPx23

  // Ideal boost to make span === areaWidth
  const idealBoost = currentSpan > 0 ? areaWidth / currentSpan : 1.0;

  // Cap so that no product exceeds the height budget
  const maxBoostH = Math.min(
    (areaHeight * heightFraction) / img1.getScaledHeight(),
    (areaHeight * image2HeightFraction) / img2.getScaledHeight(),
    (areaHeight * heightFraction) / img3.getScaledHeight(),
  )
  const overlapBoost = Math.min(idealBoost, maxBoostH)

  // Apply boost
  img1.scale(finalScale1 * overlapBoost)
  img2.scale(finalScale2 * overlapBoost)
  img3.scale(finalScale3 * overlapBoost)

  // Recompute rotated bounding boxes with boosted scale
  rotBBoxW1 = img1.getScaledWidth() * abscos + img1.getScaledHeight() * abssin
  rotBBoxH1 = img1.getScaledWidth() * abssin + img1.getScaledHeight() * abscos
  rotBBoxW2 = img2.getScaledWidth()
  rotBBoxH2 = img2.getScaledHeight()
  rotBBoxW3 = img3.getScaledWidth() * abscos + img3.getScaledHeight() * abssin
  rotBBoxH3 = img3.getScaledWidth() * abssin + img3.getScaledHeight() * abscos

  const boostedSlantComp12 = img1.getScaledHeight() * abssin   // only img1 is tilted at this boundary
  const boostedVisOverlap12 = Math.min(img1.getScaledWidth(), img2.getScaledWidth()) * (overlapPercentage / 100)
  const boostedOverlapPx12 = boostedSlantComp12 + boostedVisOverlap12

  const boostedSlantComp23 = img3.getScaledHeight() * abssin   // only img3 is tilted at this boundary
  const boostedVisOverlap23 = Math.min(img2.getScaledWidth(), img3.getScaledWidth()) * (overlapPercentage / 100)
  const boostedOverlapPx23 = boostedSlantComp23 + boostedVisOverlap23

  // --- Positioning (right-to-left: img1 rightmost, img3 leftmost) ---
  const totalGroupWidth = rotBBoxW1 + rotBBoxW2 + rotBBoxW3 - boostedOverlapPx12 - boostedOverlapPx23
  const maxGroupHeight = Math.max(rotBBoxH1, rotBBoxH2, rotBBoxH3)

  const currentGroupCenterY = baseBottom - maxGroupHeight / 2

  const targetGroupCenterY = areaTop + areaHeight / 2
  const shiftY = targetGroupCenterY - currentGroupCenterY

  const leftEmptySpace = (baseRight - totalGroupWidth) - areaLeft
  let horizontalShift = 0
  if (leftEmptySpace > 0.05 * areaWidth) {
    const offsetFraction = Math.min(leftEmptySpace / areaWidth, 0.15)
    horizontalShift = -(offsetFraction * areaWidth)
  }

  // img1: right edge of its rotBBox flush with areaRight, then shifted
  const img1CenterX = baseRight - rotBBoxW1 / 2 + horizontalShift
  const img1CenterY = baseBottom - rotBBoxH1 / 2 + shiftY

  // img2: placed to the left of img1 with overlap, then shifted
  const img2CenterX = img1CenterX - rotBBoxW1 / 2 - rotBBoxW2 / 2 + boostedOverlapPx12
  const img2CenterY = baseBottom - rotBBoxH2 / 2 + shiftY

  // img3: placed to the left of img2 with overlap, then shifted
  const img3CenterX = img2CenterX - rotBBoxW2 / 2 - rotBBoxW3 / 2 + boostedOverlapPx23
  const img3CenterY = baseBottom - rotBBoxH3 / 2 + shiftY

  img1.set({
    left: img1CenterX,
    top: img1CenterY,
    angle: fixedAngle,
    originX: 'center',
    originY: 'center',
    opacity: 1,
    layerName: 'Product1',
    shadow:
      lighting === DIRECTIONS.BEHIND ? undefined : createShadow(img1, placementType, lighting)
  })

  img2.set({
    left: img2CenterX,
    top: img2CenterY,
    angle: 0,
    originX: 'center',
    originY: 'center',
    opacity: 1,
    layerName: 'Product2',
    shadow:
      lighting === DIRECTIONS.BEHIND ? undefined : createShadow(img2, placementType, lighting)
  })

  img3.set({
    left: img3CenterX,
    top: img3CenterY,
    angle: -fixedAngle,
    originX: 'center',
    originY: 'center',
    opacity: 1,
    layerName: 'Product3',
    shadow:
      lighting === DIRECTIONS.BEHIND ? undefined : createShadow(img3, placementType, lighting)
  })

  canvas.add(img1)
  canvas.add(img2)
  canvas.add(img3)
  canvas.bringObjectToFront(img2)

  if (lighting === DIRECTIONS.BEHIND) {
    const sortedImages = images
      .map((img: any) => ({ img, index: canvas.getObjects().indexOf(img) }))
      .sort((a: any, b: any) => a.index - b.index)
      .map((item: any) => item.img)
    // Calculate bounding box
    const boundingRect = sortedImages.reduce(
      (acc: any, img: any) => {
        const { left, top } = img.getBoundingRect()
        return {
          left: Math.min(acc.left, left),
          top: Math.min(acc.top, top),
          right: Math.max(acc.right, left + img.getScaledWidth()),
          bottom: Math.max(acc.bottom, top + img.getScaledHeight()),
        }
      },
      {
        left: Infinity,
        top: Infinity,
        right: -Infinity,
        bottom: -Infinity,
      },
    )

    // Create group
    const group = new Group([], {
      left: boundingRect.left,
      top: boundingRect.top,
      originX: 'left',
      originY: 'top',
      subTargetCheck: true,
      interactive: false,
    })

    sortedImages.forEach((img: any) => {
      const center = img.getPointByOrigin('left', 'top')

      const relativeLeft = center.x - boundingRect.left
      const relativeTop = center.y - boundingRect.top

      const cloned = new FabricImage(img.getElement(), {
        left: relativeLeft,
        top: relativeTop,
        scaleX: img.scaleX,
        scaleY: img.scaleY,
        angle: img.angle,
        originX: 'left',
        originY: 'top',
        objectCaching: false,
        layerName: img.layerName,
      })

      canvas.remove(img)
      group.add(cloned)
    })
    group.set({
      left: boundingRect.left,
      top: boundingRect.top,
    })
    group.setCoords()
    group.set('shadow', createGlowShadow(group))
    group.set('layerName', 'glowGroup')
    canvas.add(group)
    canvas.bringObjectToFront(group)
    canvas.requestRenderAll()
  }

  const canvasStore = useCanvasStore()
  canvasStore.setHideLoader(true)
}

function renderCornerPlacement(
  canvas: Canvas,
  images: FabricImage[],
  mainShadow: string,
  lighting: string,
  placementType: string,
  areAllEdibleProducts: boolean = false,
  angle: number = 0,
) {
  const placementArea = areAllEdibleProducts ? getPlacementBounds('maxProductFood') : getPlacementBounds('corner3')
  const areaLeft = placementArea.left
  const areaTop = placementArea.top
  const areaRight = placementArea.right
  const areaBottom = placementArea.bottom

  const areaWidth = areaRight - areaLeft
  const areaHeight = areaBottom - areaTop
  const baseRight = areaRight
  const baseBottom = areaBottom
  const horizontalProductsCount = images.filter((img: any) => img.aspectType === PRODUCT_ASPECT_TYPES.HORIZONTAL).length
  if (horizontalProductsCount === 3) {
    renderCornerHorizontalPlacement(
      canvas,
      images,
      mainShadow,
      lighting,
      placementType,
      Math.abs(angle) || 20,
    )
    return
  }

  const shuffle = angle < 0 ? 1 : 2

  const img1 = images.reduce((verticalProd, current) =>
    current.height / current.width > verticalProd.height / verticalProd.width ? current : verticalProd,
  )
  const secondaryImgs = images.filter((img) => img !== img1)
  const img2 = secondaryImgs.reduce((verticalProd, current) =>
    current.height / current.width > verticalProd.height / verticalProd.width ? current : verticalProd,
  )
  const img3 = secondaryImgs.filter((img) => img !== img2)[0]

  const overlapRatio12 = shuffle === 1 ? 0.3 : 0.08
  const overlapRatio23 = shuffle === 1 ? 0.3 : 0.13
  const img2TiltAngle = shuffle === 1 ? 0 : -6
  const img3TiltAngle = shuffle === 1 ? 0 : -12
  const img2AngleRad = (img2TiltAngle * Math.PI) / 180
  const img3AngleRad = (img3TiltAngle * Math.PI) / 180
  const img2AbsCos = Math.abs(Math.cos(img2AngleRad))
  const img2AbsSin = Math.abs(Math.sin(img2AngleRad))
  const img3AbsCos = Math.abs(Math.cos(img3AngleRad))
  const img3AbsSin = Math.abs(Math.sin(img3AngleRad))

  const aspect1 = img1.width / img1.height
  const aspect2 = img2.width / img2.height
  const aspect3 = img3.width / img3.height
  const { share1, share2, share3 } = getWidthSplitWithRotationPenaltyFor3Products(
    aspect1, aspect2, aspect3, 0, img2AngleRad, img3AngleRad
  )

  // Slot-fit scale: img1 fills its width share + full area height,
  // img2 capped at 95% of img1's height, img3 capped at 90% of img2's (rotated) height.
  const scale1 = Math.min((areaWidth * share1) / img1.width, areaHeight / img1.height)
  img1.scale(scale1)

  const scale2 = Math.min(
    (areaWidth * share2) / (img2.width * img2AbsCos + img2.height * img2AbsSin),
    (0.95 * img1.getScaledHeight()) / (img2.width * img2AbsSin + img2.height * img2AbsCos),
  )
  img2.scale(scale2)

  const rotH2ForCap = img2.getScaledWidth() * img2AbsSin + img2.getScaledHeight() * img2AbsCos
  const scale3 = Math.min(
    (areaWidth * share3) / (img3.width * img3AbsCos + img3.height * img3AbsSin),
    (0.90 * rotH2ForCap) / (img3.width * img3AbsSin + img3.height * img3AbsCos),
  )
  img3.scale(scale3)

  // Overlap-chain layout, img1 anchored to the area's right edge (originX:'right', originY:'center').
  const buildLayout = () => {
    const W1 = img1.getScaledWidth()
    const H1 = img1.getScaledHeight()
    const W2 = img2.getScaledWidth()
    const H2 = img2.getScaledHeight()
    const W3 = img3.getScaledWidth()
    const H3 = img3.getScaledHeight()
    const rotW2 = W2 * img2AbsCos + H2 * img2AbsSin
    const rotH2 = W2 * img2AbsSin + H2 * img2AbsCos
    const rotW3 = W3 * img3AbsCos + H3 * img3AbsSin
    const rotH3 = W3 * img3AbsSin + H3 * img3AbsCos

    const overlapPx12 = H2 * img2AbsSin + Math.min(W1, rotW2) * overlapRatio12
    const overlapPx23 = Math.min(H2 * img2AbsSin, H3 * img3AbsSin) + Math.min(rotW2, rotW3) * overlapRatio23

    const px1 = baseRight
    const py1 = baseBottom - H1 / 2

    const pivotOffsetY2 = (W2 / 2) * Math.sin(img2AngleRad)
    const px2 = px1 - W1 + overlapPx12
    const py2 = baseBottom - rotH2 / 2 + pivotOffsetY2

    const pivotOffsetY3 = (W3 / 2) * Math.sin(img3AngleRad)
    const px3 = px2 - rotW2 + overlapPx23
    const py3 = baseBottom - rotH3 / 2 + pivotOffsetY3

    return { W1, H1, W2, H2, W3, H3, px1, py1, px2, py2, px3, py3 }
  }

  let layout = buildLayout()

  // Group bbox -> single boost factor that distributes leftover space across all 3 products.
  const leftMost = (W: number, H: number, px: number, absCos: number, absSin: number) => px - (W * absCos + (H / 2) * absSin)
  const topMost = (H: number, py: number, absCos: number) => py - (H / 2) * absCos

  const minLeft = Math.min(
    leftMost(layout.W1, layout.H1, layout.px1, 1, 0),
    leftMost(layout.W2, layout.H2, layout.px2, img2AbsCos, img2AbsSin),
    leftMost(layout.W3, layout.H3, layout.px3, img3AbsCos, img3AbsSin),
  )
  const minTop = Math.min(
    topMost(layout.H1, layout.py1, 1),
    topMost(layout.H2, layout.py2, img2AbsCos),
    topMost(layout.H3, layout.py3, img3AbsCos),
  )

  const distLeft = baseRight - minLeft
  const distTop = baseBottom - minTop
  const F = Math.min(areaWidth / distLeft, areaHeight / distTop)

  img1.scale(scale1 * F)
  img2.scale(scale2 * F)
  img3.scale(scale3 * F)

  // Recompute overlaps & final positions at the boosted scale.
  layout = buildLayout()

  img1.set({
    left: layout.px1,
    top: layout.py1,
    angle: 0,
    originX: 'right',
    originY: 'center',
    opacity: 1,
    shadow: lighting === DIRECTIONS.BEHIND ? undefined : createShadow(img1, placementType, lighting),
    layerName: 'Product1',
  })

  img2.set({
    left: layout.px2,
    top: layout.py2,
    angle: img2TiltAngle,
    originX: 'right',
    originY: 'center',
    opacity: 1,
    shadow: lighting === DIRECTIONS.BEHIND ? undefined : createShadow(img2, placementType, lighting),
    layerName: 'Product2',
  })

  img3.set({
    left: layout.px3,
    top: layout.py3,
    angle: img3TiltAngle,
    originX: 'right',
    originY: 'center',
    opacity: 1,
    shadow: lighting === DIRECTIONS.BEHIND ? undefined : createShadow(img3, placementType, lighting),
    layerName: 'Product3',
  })

  canvas.add(img1)
  canvas.add(img2)
  canvas.add(img3)
  if (shuffle === 1) {
    canvas.bringObjectToFront(img2)
  } else {
    canvas.bringObjectToFront(img2)
    canvas.bringObjectToFront(img1)
  }
  img3.setCoords()
  if (lighting === DIRECTIONS.BEHIND) {
    // Calculate the bounding box of all images
    const sortedImages = images
      .map((img: any) => ({ img, index: canvas.getObjects().indexOf(img) }))
      .sort((a: any, b: any) => a.index - b.index)
      .map((item: any) => item.img)

    // Calculate bounding box
    const boundingRect = sortedImages.reduce(
      (acc: any, img: any) => {
        const { left, top } = img.getBoundingRect()

        return {
          left: Math.min(acc.left, left),
          top: Math.min(acc.top, top),
          right: Math.max(acc.right, left + img.getScaledWidth()),
          bottom: Math.max(acc.bottom, top + img.getScaledHeight()),
        }
      },
      {
        left: Infinity,
        top: Infinity,
        right: -Infinity,
        bottom: -Infinity,
      },
    )

    // Create group
    const group = new Group([], {
      left: boundingRect.left,
      top: boundingRect.top,
      originX: 'left',
      originY: 'top',
      subTargetCheck: true,
      interactive: false,
    })

    sortedImages.forEach((img: any) => {
      const center = img.getPointByOrigin('left', 'top')

      const relativeLeft = center.x - boundingRect.left
      const relativeTop = center.y - boundingRect.top

      const cloned = new FabricImage(img.getElement(), {
        left: relativeLeft,
        top: relativeTop,
        scaleX: img.scaleX,
        scaleY: img.scaleY,
        angle: img.angle,
        originX: 'left',
        originY: 'top',
        objectCaching: false,
        layerName: img.layerName,
      })

      canvas.remove(img)
      group.add(cloned)
    })
    group.set({
      left: boundingRect.left,
      top: boundingRect.top,
    })
    group.set('shadow', createGlowShadow(group))
    group.set('layerName', 'glowGroup')
    group.setCoords()
    canvas.add(group)
    canvas.bringObjectToFront(group)
    canvas.requestRenderAll()
  }

  const canvasStore = useCanvasStore()
  canvasStore.setHideLoader(true)
}

export function renderCornerHorizontalPlacement(
  canvas: Canvas,
  images: FabricImage[],
  mainShadow: string,
  lighting: string,
  placementType: string,
  mainAngle: number = 20,
) {
  const placementArea = getPlacementBounds('corner3')
  const areaLeft = placementArea.left
  const areaTop = placementArea.top
  const areaWidth = placementArea.right - areaLeft
  const areaHeight = placementArea.bottom - areaTop

  const baseRight = placementArea.right
  const baseBottom = placementArea.bottom

  // Step 1: Decide the images order
  // first image is less widest and img3 should be the most widest
  const sortedImages = [...images].sort((a, b) => (b.width / b.height) - (a.width / a.height))
  const img1 = sortedImages[0]
  const img2 = sortedImages[1]
  const img3 = sortedImages[2]

  // Step 2: Decide the angles for each of the image
  const angle1 = 12
  const angle2 = 22
  const angle3 = 38
  const rad1 = (angle1 * Math.PI) / 180
  const rad2 = (angle2 * Math.PI) / 180
  const rad3 = (angle3 * Math.PI) / 180

  // Step 3: Find the max scale in the regions of height / 3, width
  const heightPerProd = areaHeight / 3
  const initialScales = [img1, img2, img3].map(img => Math.min(heightPerProd / img.height, areaWidth / img.width))

  const W1 = img1.width * initialScales[0]
  const H1 = img1.height * initialScales[0]
  const W2 = img2.width * initialScales[1]
  const H2 = img2.height * initialScales[1]
  const W3 = img3.width * initialScales[2]
  const H3 = img3.height * initialScales[2]

  const rotW1 = W1 * Math.cos(rad1) + H1 * Math.sin(rad1)
  const rotH1 = W1 * Math.sin(rad1) + H1 * Math.cos(rad1)

  const rotW2 = W2 * Math.cos(rad2) + H2 * Math.sin(rad2)
  const rotH2 = W2 * Math.sin(rad2) + H2 * Math.cos(rad2)

  const rotW3 = W3 * Math.cos(rad3) + H3 * Math.sin(rad3)
  const rotH3 = W3 * Math.sin(rad3) + H3 * Math.cos(rad3)

  // Step 4: offset for first product
  const px1 = baseRight + rotW1 * 0.08
  const py1 = baseBottom + rotH1 * 0.08

  // Step 5: offset for second product
  // Shift left based on rotated width
  const px2 = baseRight + rotW2 * 0.10
  // Visually overlap taking into account the horizontal shift and angle climb
  const py2 = py1 + Math.tan(rad1) * (px2 - px1) - 0.40 * (H1 / Math.cos(rad1))

  // Step 6: offset for third product
  const px3 = baseRight + rotW3 * 0.12
  const py3 = py2 + Math.tan(rad2) * (px3 - px2) - 0.40 * (H2 / Math.cos(rad2))

  // Step 7: find max scale for entire arrangement
  const getLeftMost = (W: number, H: number, px: number, angleRad: number) => px - W * Math.cos(angleRad)
  const getTopMost = (W: number, H: number, py: number, angleRad: number) => py - W * Math.sin(angleRad) - H * Math.cos(angleRad)

  const lefts = [
    getLeftMost(W1, H1, px1, rad1),
    getLeftMost(W2, H2, px2, rad2),
    getLeftMost(W3, H3, px3, rad3)
  ]
  const tops = [
    getTopMost(W1, H1, py1, rad1),
    getTopMost(W2, H2, py2, rad2),
    getTopMost(W3, H3, py3, rad3)
  ]

  const minLeft = Math.min(...lefts)
  const minTop = Math.min(...tops)

  const distLeft = baseRight - minLeft
  const distTop = baseBottom - minTop

  const F_width = areaWidth / distLeft
  const F_height = areaHeight / distTop
  const F = Math.min(F_width, F_height)

  // Step 8: Apply min scale F to everything
  const finalScale1 = initialScales[0] * F
  const finalScale2 = initialScales[1] * F
  const finalScale3 = initialScales[2] * F

  img1.scale(finalScale1)
  img2.scale(finalScale2)
  img3.scale(finalScale3)

  // Recompute positions based on new scales
  const finalW1 = img1.getScaledWidth()
  const finalH1 = img1.getScaledHeight()
  const finalW2 = img2.getScaledWidth()
  const finalH2 = img2.getScaledHeight()
  const finalW3 = img3.getScaledWidth()
  const finalH3 = img3.getScaledHeight()

  const finalRotW1 = finalW1 * Math.cos(rad1) + finalH1 * Math.sin(rad1)
  const finalRotH1 = finalW1 * Math.sin(rad1) + finalH1 * Math.cos(rad1)

  const finalRotW2 = finalW2 * Math.cos(rad2) + finalH2 * Math.sin(rad2)
  const finalRotH2 = finalW2 * Math.sin(rad2) + finalH2 * Math.cos(rad2)

  const finalRotW3 = finalW3 * Math.cos(rad3) + finalH3 * Math.sin(rad3)
  const finalRotH3 = finalW3 * Math.sin(rad3) + finalH3 * Math.cos(rad3)

  const finalPx1 = baseRight + finalRotW1 * 0.08
  const finalPy1 = baseBottom + finalRotH1 * 0.08

  const finalPx2 = baseRight + finalRotW2 * 0.10
  const finalPy2 = finalPy1 + Math.tan(rad1) * (finalPx2 - finalPx1) - 0.40 * (finalH1 / Math.cos(rad1))

  const finalPx3 = baseRight + finalRotW3 * 0.12
  const finalPy3 = finalPy2 + Math.tan(rad2) * (finalPx3 - finalPx2) - 0.40 * (finalH2 / Math.cos(rad2))

  img1.set({
    left: finalPx1,
    top: finalPy1,
    angle: angle1,
    originX: 'right',
    originY: 'bottom',
    opacity: 1,
    shadow: lighting === DIRECTIONS.BEHIND ? null : createShadow(img1, placementType, lighting),
    layerName: 'Product1',
  })

  img2.set({
    left: finalPx2,
    top: finalPy2,
    angle: angle2,
    originX: 'right',
    originY: 'bottom',
    opacity: 1,
    shadow: lighting === DIRECTIONS.BEHIND ? null : createShadow(img2, placementType, lighting),
    layerName: 'Product2',
  })

  img3.set({
    left: finalPx3,
    top: finalPy3,
    angle: angle3,
    originX: 'right',
    originY: 'bottom',
    opacity: 1,
    shadow: lighting === DIRECTIONS.BEHIND ? null : createShadow(img3, placementType, lighting),
    layerName: 'Product3',
  })

  canvas.add(img1)
  canvas.add(img2)
  canvas.add(img3)

  if (lighting === DIRECTIONS.BEHIND) {
    const sortedImages = images
      .map((img: any) => ({ img, index: canvas.getObjects().indexOf(img) }))
      .sort((a: any, b: any) => a.index - b.index)
      .map((item: any) => item.img)

    // Calculate bounding box
    const boundingRect = sortedImages.reduce(
      (acc: any, img: any) => {
        const { left, top } = img.getBoundingRect()

        return {
          left: Math.min(acc.left, left),
          top: Math.min(acc.top, top),
          right: Math.max(acc.right, left + img.getScaledWidth()),
          bottom: Math.max(acc.bottom, top + img.getScaledHeight()),
        }
      },
      {
        left: Infinity,
        top: Infinity,
        right: -Infinity,
        bottom: -Infinity,
      },
    )

    // Create group
    const group = new Group([], {
      left: boundingRect.left,
      top: boundingRect.top,
      originX: 'left',
      originY: 'top',
      subTargetCheck: true,
      interactive: false,
    })

    sortedImages.forEach((img: any) => {
      const center = img.getPointByOrigin('left', 'top')

      const relativeLeft = center.x - boundingRect.left
      const relativeTop = center.y - boundingRect.top

      const cloned = new FabricImage(img.getElement(), {
        left: relativeLeft,
        top: relativeTop,
        scaleX: img.scaleX,
        scaleY: img.scaleY,
        angle: img.angle,
        originX: 'left',
        originY: 'top',
        objectCaching: false,
        layerName: img.layerName,
      })

      canvas.remove(img)
      group.add(cloned)
    })
    group.set({
      left: boundingRect.left,
      top: boundingRect.top,
    })
    group.setCoords()
    group.set('shadow', createGlowShadow(group))
    group.set('layerName', 'glowGroup')
    canvas.add(group)
    canvas.bringObjectToFront(group)
  }
  canvas.renderAll()
  const canvasStore = useCanvasStore()
  canvasStore.setHideLoader(true)
}

export async function renderStaircaseTriplePlacement(
  canvas: Canvas,
  images: FabricImage[],
  tiltAngle: number,
  lightDirection: string,
  placementType: string,
  areAllEdibleProducts: boolean,
) {
  if (images.length !== 3) return
  const overlapStyle: string = 'overlap'

  const placementArea = areAllEdibleProducts ? getPlacementBounds('maxProductFood') : getPlacementBounds('staircase3')
  const placementWidth = placementArea.right - placementArea.left
  const placementHeight = placementArea.bottom - placementArea.top

  const sorted = [...images].sort((a: any, b: any) => ASPECT_TYPE_ORDER_FOR_STAIRCASE[a.aspectType] - ASPECT_TYPE_ORDER_FOR_STAIRCASE[b.aspectType])
  const [leftImg, middleImg, rightImg] = sorted // left = Horizontal, middle = Square/BoxH, right = Vertical/BoxV right = tallest

  const aspect1 = leftImg.width / leftImg.height
  const aspect2 = middleImg.width / middleImg.height
  const aspect3 = rightImg.width / rightImg.height

  const { share1, share2, share3 } = getWidthSplitWithRotationPenaltyFor3Products(aspect1, aspect2, aspect3, 0, 0, 0, 0.30, 0.35)
  const { overlapFraction1, overlapFraction2 } = getOverlapPercentageForStaircase((leftImg as any).aspectType, (middleImg as any).aspectType, (rightImg as any).aspectType)
  const finalScale1 = Math.min((placementWidth * share1) / leftImg.width, placementHeight / leftImg.height)
  const finalScale2 = Math.min((placementWidth * share2) / middleImg.width, placementHeight / middleImg.height)
  const finalScale3 = Math.min((placementWidth * share3) / rightImg.width, placementHeight / rightImg.height)

  leftImg.scale(finalScale1)
  middleImg.scale(finalScale2)
  rightImg.scale(finalScale3)

  let overlapPx1 = Math.min(leftImg.getScaledWidth(), middleImg.getScaledWidth()) * overlapFraction1
  let overlapPx2 = Math.min(middleImg.getScaledWidth(), rightImg.getScaledWidth()) * overlapFraction2

  const combinedWidth = leftImg.getScaledWidth() + middleImg.getScaledWidth() + rightImg.getScaledWidth() - overlapPx1 - overlapPx2
  const widthScaleFactor = placementWidth / combinedWidth
  const heightScaleFactor = placementHeight / Math.max(leftImg.getScaledHeight(), middleImg.getScaledHeight(), rightImg.getScaledHeight())
  const boostScale = Math.min(widthScaleFactor, heightScaleFactor)

  leftImg.scale(finalScale1 * boostScale)
  middleImg.scale(finalScale2 * boostScale)
  rightImg.scale(finalScale3 * boostScale)

  overlapPx1 = Math.min(leftImg.getScaledWidth(), middleImg.getScaledWidth()) * overlapFraction1
  overlapPx2 = Math.min(middleImg.getScaledWidth(), rightImg.getScaledWidth()) * overlapFraction2

  const left3 = placementArea.right - rightImg.getScaledWidth() / 2
  const top3 = placementArea.bottom - rightImg.getScaledHeight() / 2
  const left2 = left3 - rightImg.getScaledWidth() / 2 - middleImg.getScaledWidth() / 2 + overlapPx2
  const top2 = placementArea.bottom - middleImg.getScaledHeight() / 2
  const left1 = left2 - middleImg.getScaledWidth() / 2 - leftImg.getScaledWidth() / 2 + overlapPx1
  const top1 = placementArea.bottom - leftImg.getScaledHeight() / 2

  const groupTopRaw = Math.min(
    top1 - leftImg.getScaledHeight() / 2,
    top2 - rightImg.getScaledHeight() / 2,
    top3 - rightImg.getScaledHeight() / 2
  );
  const groupBottomRaw = Math.max(
    top1 + leftImg.getScaledHeight() / 2,
    top2 + rightImg.getScaledHeight() / 2,
    top3 + rightImg.getScaledHeight() / 2
  );

  const groupTotalHeight = groupBottomRaw - groupTopRaw;
  const desiredGroupTop = placementArea.top + (placementHeight - groupTotalHeight) / 2;
  const verticalOffset = (desiredGroupTop - groupTopRaw) * 0.25;

  leftImg.set({
    left: left1,
    top: top1 + verticalOffset,
    originX: 'center',
    originY: 'center',
    angle: 0,
    layerName: 'Product1',
  })

  middleImg.set({
    left: left2,
    top: top2 + verticalOffset,
    originX: 'center',
    originY: 'center',
    angle: 0,
    layerName: 'Product2',
  })

  rightImg.set({
    left: left3,
    top: top3 + verticalOffset,
    originX: 'center',
    originY: 'center',
    angle: 0,
    layerName: 'Product3',
  })

  canvas.add(leftImg)
  canvas.add(middleImg)
  canvas.add(rightImg)


  canvas.bringObjectToFront(middleImg) // bring above right
  canvas.bringObjectToFront(leftImg) // bring above middle

  let isAiShadowApplied = false
  try {
    await generateAIShadows(canvas)
    isAiShadowApplied = true
  } catch { }

  if (lightDirection != DIRECTIONS.NONE && !isAiShadowApplied) {
    // Shadow
    await addStaircaseShadow(
      leftImg,
      canvas,
      0,
      images.some((img: any) => img.isMultiple),
    )
    await addStaircaseShadow(
      middleImg,
      canvas,
      0,
      images.some((img: any) => img.isMultiple),
    )
    await addStaircaseShadow(
      rightImg,
      canvas,
      0,
      images.some((img: any) => img.isMultiple),
    )
  }

  canvas.bringObjectToFront(middleImg) // bring above right
  canvas.bringObjectToFront(leftImg) // bring above middle

  canvas.renderAll()

  const canvasStore = useCanvasStore()
  canvasStore.setHideLoader(true)
}

export async function renderStaircasePairPlacement(
  canvas: Canvas,
  images: FabricImage[],
  tiltAngle: number,
  lightDirection: string,
  placementType: string,
  areAllEdibleProducts: boolean,
) {
  if (images.length !== 2) return
  const placementArea = areAllEdibleProducts ? getPlacementBounds('maxProductFood') : getPlacementBounds('staircase2')
  const placementWidth = placementArea.right - placementArea.left
  const placementHeight = placementArea.bottom - placementArea.top

  const sorted = [...images].sort((a: any, b: any) => ASPECT_TYPE_ORDER_FOR_STAIRCASE[a.aspectType] - ASPECT_TYPE_ORDER_FOR_STAIRCASE[b.aspectType])
  const leftImg = sorted[0] // widest aspect (e.g. Horizontal)
  const rightImg = sorted[1] // narrowest aspect (e.g. Vertical)
  const leftImgAspectRatio = leftImg.width / leftImg.height
  const rightImgAspectRatio = rightImg.width / rightImg.height

  const { share1, share2 } = getWidthSplitWithRotationPenaltyFor2Products(leftImgAspectRatio, rightImgAspectRatio, 0, 0, 0, 0.4, 0.6)

  const rightImgScale = Math.min((placementWidth * share2) / rightImg.width, (placementHeight) / rightImg.height)
  const lefImgMaxHeight = rightImg.height * rightImgScale * 0.80
  const leftImgScale = Math.min((placementWidth * share1) / leftImg.width, (Math.min(lefImgMaxHeight, placementHeight)) / leftImg.height)

  leftImg.scale(leftImgScale)
  rightImg.scale(rightImgScale)

  const overlapPercentage = getRandomNumber(30, 0, 5);
  let overlapPx = Math.min(leftImg.getScaledWidth(), rightImg.getScaledWidth()) * overlapPercentage / 100;
  const toTalWidth = leftImg.getScaledWidth() + rightImg.getScaledWidth() - overlapPx;

  const widthScaleFactor = placementWidth / toTalWidth;
  const heightScaleFactor = placementHeight / Math.max(leftImg.getScaledHeight(), rightImg.getScaledHeight());
  const boostScale = Math.min(widthScaleFactor, heightScaleFactor);

  leftImg.scale(leftImgScale * boostScale);
  rightImg.scale(rightImgScale * boostScale);

  overlapPx = Math.min(leftImg.getScaledWidth(), rightImg.getScaledWidth()) * overlapPercentage / 100;

  const left1 = placementArea.left + leftImg.getScaledWidth() / 2;
  const left2 = left1 + (leftImg.getScaledWidth() / 2) + (rightImg.getScaledWidth() / 2) - overlapPx;
  const top1 = placementArea.bottom - leftImg.getScaledHeight() / 2;
  const top2 = placementArea.bottom - rightImg.getScaledHeight() / 2;

  const groupLeftRaw = Math.min(
    left1 - leftImg.getScaledWidth() / 2,
    left2 - rightImg.getScaledWidth() / 2
  );
  const groupRightRaw = Math.max(
    left1 + leftImg.getScaledWidth() / 2,
    left2 + rightImg.getScaledWidth() / 2
  );
  const groupTopRaw = Math.min(
    top1 - leftImg.getScaledHeight() / 2,
    top2 - rightImg.getScaledHeight() / 2
  );
  const groupBottomRaw = Math.max(
    top1 + leftImg.getScaledHeight() / 2,
    top2 + rightImg.getScaledHeight() / 2
  );

  const groupTotalWidth = groupRightRaw - groupLeftRaw;
  const groupTotalHeight = groupBottomRaw - groupTopRaw;

  const desiredGroupLeft = placementArea.left + (placementWidth - groupTotalWidth) / 2;
  const desiredGroupTop = placementArea.top + (placementHeight - groupTotalHeight) / 2;

  const horizontalOffset = desiredGroupLeft - groupLeftRaw;
  // The empty vertical space is divide as 25% for bottom and 75% for top
  const verticalOffset = (desiredGroupTop - groupTopRaw) * 0.5;

  leftImg.set({
    left: left1 + horizontalOffset,
    top: top1 + verticalOffset,
    originX: 'center',
    originY: 'center',
    angle: 0,
    layerName: 'Product1',
  })

  rightImg.set({
    left: left2 + horizontalOffset,
    top: top2 + verticalOffset,
    originX: 'center',
    originY: 'center',
    angle: 0,
    layerName: 'Product2',
  })

  canvas.add(leftImg)
  canvas.add(rightImg)
  canvas.bringObjectToFront(leftImg)

  let isAiShadowApplied = false
  try {
    await generateAIShadows(canvas)
    isAiShadowApplied = true
  } catch { }

  if (lightDirection != DIRECTIONS.NONE && !isAiShadowApplied) {
    // Shadow
    await addStaircaseShadow(
      leftImg,
      canvas,
      0,
      images.some((img: any) => img.isMultiple),
    )
    await addStaircaseShadow(
      rightImg,
      canvas,
      0,
      images.some((img: any) => img.isMultiple),
    )
  }

  // Ensure the visually smaller one comes on top (check final *scaled dimensions*)
  const topImage =
    leftImg.getScaledHeight() < rightImg.getScaledHeight() ||
      leftImg.getScaledWidth() < rightImg.getScaledWidth()
      ? leftImg
      : rightImg

  canvas.bringObjectToFront(topImage)
  canvas.renderAll()

  const canvasStore = useCanvasStore()
  canvasStore.setHideLoader(true)
}

// FRONT PLACEMENT OF TWO PRODUCTS
export async function renderFrontPairPlacement(
  canvas: Canvas,
  images: FabricImage[],
  tiltAngle: number,
  lightDirection: string,
  placementType: string,
  aspectCounts: AspectCounts | null = null,
  areAllEdibleProducts: boolean = false,
) {
  const frontPlacementFabricImages: FabricImage[] = []
  extendToObject()

  if (images.some((img: any) => img.isMultiple)) {
    tiltAngle = 0
  }

  const placementArea = getPlacementBounds('front2')
  if (areAllEdibleProducts) {
    placementArea.left = getPlacementBounds('maxProductFood').left;
  }
  const areaLeft = placementArea.left
  const areaTop = placementArea.top
  const areaRight = placementArea.right
  const areaBottom = placementArea.bottom
  const areaWidth = areaRight - areaLeft
  const areaHeight = areaBottom - areaTop

  const img2 = images.reduce((widest, current) =>
    current.width / current.height > widest.width / widest.height ? current : widest,
  )
  const secondaryImgs = images.filter((img) => img !== img2)
  const [img1] = secondaryImgs
  const aspect1 = img1.width / img1.height
  const aspect2 = img2.width / img2.height
  const randomTiltAngle = tiltAngle === 0 ? 0 : getRandomNumber(tiltAngle, -10, 10)
  const areAllProdsTall = aspectCounts && (aspectCounts.Vertical + aspectCounts.BoxVertical) === 2 ? true : false
  const gapFractionBetweenProducts = areAllProdsTall ? 0.01 : 0
  const overlapPercentage = areAllProdsTall ? 0 : getRandomNumber(15, 0, 15)

  // --- Intelligent width split ---
  // Each product gets a share of the total width proportional to its aspect ratio
  // (wider products naturally need more horizontal space).
  // The share is clamped so neither product is starved of space.
  const angleRad = (randomTiltAngle * Math.PI) / 180
  const { share1, share2 } = getWidthSplitWithRotationPenaltyFor2Products(aspect1, aspect2, gapFractionBetweenProducts, angleRad, angleRad, 0.45, 0.55)
  const HEIGHT_FRACTION = 0.98

  const abscos = Math.abs(Math.cos(angleRad))
  const abssin = Math.abs(Math.sin(angleRad))

  // Scale each product so it fits its allocated width slot AND height budget
  // AFTER rotation. The axis-aligned bounding box of a rotated rectangle is:
  //   rotatedW = w·|cosθ| + h·|sinθ|
  //   rotatedH = w·|sinθ| + h·|cosθ|
  // This guarantees the image never overflows its slot once tilted.
  const finalScale1 = Math.min(
    (areaWidth * share1) / (img1.width * abscos + img1.height * abssin),
    (areaHeight * HEIGHT_FRACTION) / (img1.width * abssin + img1.height * abscos),
  )
  const finalScale2 = Math.min(
    (areaWidth * share2) / (img2.width * abscos + img2.height * abssin),
    (areaHeight * HEIGHT_FRACTION) / (img2.width * abssin + img2.height * abscos),
  )
  img1.scale(finalScale1)
  img2.scale(finalScale2)

  // Rotated axis-aligned bounding box of each scaled image
  let rotBBoxW1 = img1.getScaledWidth() * abscos + img1.getScaledHeight() * abssin
  let rotBBoxH1 = img1.getScaledWidth() * abssin + img1.getScaledHeight() * abscos
  let rotBBoxW2 = img2.getScaledWidth() * abscos + img2.getScaledHeight() * abssin
  let rotBBoxH2 = img2.getScaledWidth() * abssin + img2.getScaledHeight() * abscos

  // Small pixel gap between the two images
  const gapPx = areaWidth * gapFractionBetweenProducts

  // Redistribute the gap caused by overlap to both the products equally
  if (gapPx === 0) {
    const prelimSlant = Math.min(img1.getScaledHeight(), img2.getScaledHeight()) * abssin
    const prelimVisOverlap = Math.min(img1.getScaledWidth(), img2.getScaledWidth()) * (overlapPercentage / 100)
    const prelimOverlapPx = prelimSlant + prelimVisOverlap

    const currentSpan = rotBBoxW1 + rotBBoxW2 - prelimOverlapPx
    const idealBoost = currentSpan > 0 ? areaWidth / currentSpan : 1.0

    const maxBoostH = Math.min(
      (areaHeight * HEIGHT_FRACTION) / img1.getScaledHeight(),
      (areaHeight * HEIGHT_FRACTION) / img2.getScaledHeight(),
    )
    const overlapBoost = Math.min(idealBoost, maxBoostH)

    img1.scale(finalScale1 * overlapBoost)
    img2.scale(finalScale2 * overlapBoost)

    // Recompute rotBBoxes with the boosted scale
    rotBBoxW1 = img1.getScaledWidth() * abscos + img1.getScaledHeight() * abssin
    rotBBoxH1 = img1.getScaledWidth() * abssin + img1.getScaledHeight() * abscos
    rotBBoxW2 = img2.getScaledWidth() * abscos + img2.getScaledHeight() * abssin
    rotBBoxH2 = img2.getScaledWidth() * abssin + img2.getScaledHeight() * abscos
  }

  const img1CenterX = areaRight - rotBBoxW1 / 2
  let img2CenterX
  if (gapPx !== 0) {
    img2CenterX = img1CenterX - rotBBoxW1 / 2 - gapPx - rotBBoxW2 / 2
  } else {
    const slantCompensation = Math.min(img1.getScaledHeight(), img2.getScaledHeight()) * abssin
    const visualOverlapPx = Math.min(img1.getScaledWidth(), img2.getScaledWidth()) * (overlapPercentage / 100)
    const overlapPx = slantCompensation + visualOverlapPx
    img2CenterX = img1CenterX - rotBBoxW1 / 2 - rotBBoxW2 / 2 + overlapPx
  }

  const img1CenterY = areaBottom - rotBBoxH1 / 2
  const img2CenterY = areaBottom - rotBBoxH2 / 2

  img1.set({
    left: img1CenterX,
    top: img1CenterY,
    angle: randomTiltAngle,
    originX: 'center',
    originY: 'center',
    opacity: 1,
    layerName: 'Product1',
  })
  img2.set({
    left: img2CenterX,
    top: img2CenterY,
    angle: randomTiltAngle,
    originX: 'center',
    originY: 'center',
    opacity: 1,
    layerName: 'Product2',
  })

  img1.set('layerId', uuidv4())
  canvas.add(img1)
  frontPlacementFabricImages.push(img1)

  img2.set('layerId', uuidv4())
  canvas.add(img2)
  frontPlacementFabricImages.push(img2)
  canvas.bringObjectToFront(img1)
  
  let isAiShadowApplied = false
  if (randomTiltAngle == 0) {
    try {
      await generateAIShadows(canvas)
      isAiShadowApplied = true
    } catch { }
  }

  let shadowType = ''
  const newAspectRatio = (img1.height * img1.scaleY) / (img1.width * img1.scaleX)
  if (!shadowType) {
    if (Math.abs(img1.angle % 360) > 20 && Math.abs(img1.angle % 360) < 340) {
      shadowType = 'rotate'
    } else {
      shadowType = newAspectRatio > 1.5 ? 'ellipse' : 'square'
    }
  }
  if (lightDirection != DIRECTIONS.NONE) {
    if (randomTiltAngle != 0 || !isAiShadowApplied) {
      if ((img1 as any).isMultiple) {
        img1.shadow = createDropShadow(img1, DIRECTIONS.BOTTOM)
      } else {
        const shadowImage = await createContactShadow(img1, shadowType, canvas, randomTiltAngle)
        const shape = getBottomShapeLabel(img1)
        const { leftBottom, rightBottom, centerBottom } = getBottomCanvasPoints(img1, shape)
        let offsetY = 0
        if (
          (shadowImage as FabricImage & { layerType?: string }).layerType == 'ellipse' &&
          randomTiltAngle === 0
        ) {
          offsetY = shadowImage.getScaledHeight() / 2
        }

        let bottomY

        if (randomTiltAngle < 0) {
          shadowImage.left =
            leftBottom.x + shadowImage.getScaledWidth() / 2 - shadowImage.getScaledWidth() * 0.03
          bottomY = leftBottom.y
        } else if (randomTiltAngle > 0) {
          shadowImage.left =
            rightBottom.x - shadowImage.getScaledWidth() / 2 + shadowImage.getScaledWidth() * 0.03
          bottomY = rightBottom.y
        } else {
          if (shape == 'pouch') {
            bottomY = leftBottom.y + shadowImage.getScaledHeight() / 2
          } else if (shape == 'wavy') {
            bottomY =
              centerBottom.y +
              shadowImage.getScaledHeight() / 2 -
              offsetY -
              getBottomWaveCurvePoint(img1)
          }
          else if (shape == 'tilted') {
            bottomY = rightBottom.y + shadowImage.getScaledHeight() * 0.2
          } else {
            bottomY = centerBottom.y + shadowImage.getScaledHeight() / 2 - offsetY
          }
        }

        shadowImage.set({
          top: bottomY,
        })
        shadowImage.set('layerId', (img1 as FabricImage & { layerId?: string }).layerId)
        shadowImage.set('globalCompositeOperation', 'multiply')
        canvas.add(shadowImage)
        const productIndex = canvas.getObjects().indexOf(img1)
        canvas.moveObjectTo(shadowImage, productIndex)
      }
    }
  }

  shadowType = ''
  const newSecondAspectRatio = (img2.height * img2.scaleY) / (img2.width * img2.scaleX)
  if (!shadowType) {
    if (Math.abs(img2.angle % 360) > 20 && Math.abs(img2.angle % 360) < 340) {
      shadowType = 'rotate'
    } else {
      shadowType = newSecondAspectRatio > 1.5 ? 'ellipse' : 'square'
    }
  }
  if (lightDirection != DIRECTIONS.NONE) {
    if (randomTiltAngle != 0 || !isAiShadowApplied) {
      if ((img2 as any).isMultiple) {
        img2.shadow = createDropShadow(img2, DIRECTIONS.BOTTOM)
      } else {
        const shadowImage = await createContactShadow(img2, shadowType, canvas, randomTiltAngle)
        const shape = getBottomShapeLabel(img2)
        const { leftBottom, rightBottom, centerBottom } = getBottomCanvasPoints(img2, shape)
        let offsetY = 0
        if (
          (shadowImage as FabricImage & { layerType?: string }).layerType == 'ellipse' &&
          randomTiltAngle === 0
        ) {
          offsetY = shadowImage.getScaledHeight() / 2
        }

        let bottomY

        if (randomTiltAngle < 0) {
          shadowImage.left =
            leftBottom.x + shadowImage.getScaledWidth() / 2 - shadowImage.getScaledWidth() * 0.03
          bottomY = leftBottom.y
        } else if (randomTiltAngle > 0) {
          shadowImage.left =
            rightBottom.x - shadowImage.getScaledWidth() / 2 + shadowImage.getScaledWidth() * 0.03
          bottomY = rightBottom.y
        } else {
          if (shape == 'pouch') {
            bottomY = leftBottom.y + shadowImage.getScaledHeight() / 2
          } else if (shape == 'wavy') {
            bottomY =
              centerBottom.y +
              shadowImage.getScaledHeight() / 2 -
              offsetY -
              getBottomWaveCurvePoint(img2)
          } else if (shape == 'tilted') {
            bottomY = rightBottom.y + shadowImage.getScaledHeight() * 0.2
          } else {
            bottomY = centerBottom.y + shadowImage.getScaledHeight() / 2 - offsetY
          }
        }

        shadowImage.set({
          top: bottomY,
        })
        shadowImage.set('layerId', (img2 as FabricImage & { layerId?: string }).layerId)
        shadowImage.set('globalCompositeOperation', 'multiply')
        canvas.add(shadowImage)
        const productIndex = canvas.getObjects().indexOf(img2)
        canvas.moveObjectTo(shadowImage, productIndex)
      }
    }
  }
  canvas.renderAll()
  const canvasStore = useCanvasStore()
  canvasStore.setHideLoader(true)
}

// RENDER TOP PLACEMENT OF TWO PRODUCTS
export function renderTopPairPlacement(
  canvas: Canvas,
  images: FabricImage[],
  tiltAngles: number[],
  overlapStyle: string,
  lightDirection: string,
  placementType: string,
  areAllEdibleProducts: boolean = false
): void {
  const placementArea = placementType === PLACEMENT_TYPES.FLOAT ? getPlacementBounds('float2') : getPlacementBounds('top2')
  if (areAllEdibleProducts) {
    placementArea.left = Math.max(getPlacementBounds('maxProductFood').left, placementArea.left);
  }
  const areaLeft = placementArea.left
  const areaTop = placementArea.top
  const areaRight = placementArea.right
  const areaBottom = placementArea.bottom
  const areaWidth = areaRight - areaLeft
  const areaHeight = areaBottom - areaTop

  const img1 = images.reduce((widest, current) =>
    current.width / current.height > widest.width / widest.height ? current : widest,
  )
  const secondaryImgs = images.filter((img) => img !== img1)
  const [img2] = secondaryImgs
  const tiltAngle = tiltAngles[0] === 0 ? 0 : getRandomNumber(tiltAngles[0] || 0, -5, 5)

  const aspect1 = img1.width / img1.height
  const aspect2 = img2.width / img2.height
  const angleRad = (tiltAngle * Math.PI) / 180
  const abscos = Math.abs(Math.cos(angleRad))
  const abssin = Math.abs(Math.sin(angleRad))

  const style = determineProductsPlacementStyleInTopPlacement((img1 as any).aspectType, (img2 as any).aspectType, areaWidth, areaHeight)
  const percentageGapBetweenProds = style == TOP_PLACEMENT_STYLE.GAP ? getRandomNumber(0, 1, 5) / 100 : 0

  const { share1, share2 } = getWidthSplitWithRotationPenaltyFor2Products(aspect1, aspect2, percentageGapBetweenProds, angleRad, angleRad, 0.45, 0.55)

  const finalScale1 = Math.min(
    (areaWidth * share1) / (img1.width * abscos + img1.height * abssin),
    areaHeight / (img1.width * abssin + img1.height * abscos),
  )
  const finalScale2 = Math.min(
    (areaWidth * share2) / (img2.width * abscos + img2.height * abssin),
    areaHeight / (img2.width * abssin + img2.height * abscos),
  )
  img1.scale(finalScale1)
  img2.scale(finalScale2)

  // Rotated axis-aligned bounding box of each scaled image
  let rotBBoxW1 = img1.getScaledWidth() * abscos + img1.getScaledHeight() * abssin
  let rotBBoxH1 = img1.getScaledWidth() * abssin + img1.getScaledHeight() * abscos
  let rotBBoxW2 = img2.getScaledWidth() * abscos + img2.getScaledHeight() * abssin
  let rotBBoxH2 = img2.getScaledWidth() * abssin + img2.getScaledHeight() * abscos

  const gapPx = areaWidth * percentageGapBetweenProds
  const overlapPercentage = getOverlapPercentageForTopPlacement((img1 as any).aspectType, (img2 as any).aspectType)
  // Redistribut the gap caused by overlap to both the products equally
  if (style == TOP_PLACEMENT_STYLE.OVERLAP) {
    const prelimSlant = Math.min(img1.getScaledHeight(), img2.getScaledHeight()) * abssin
    const prelimVisOverlap = Math.min(img1.getScaledWidth(), img2.getScaledWidth()) * (overlapPercentage / 100)
    const prelimOverlapPx = prelimSlant + prelimVisOverlap

    const currentSpan = rotBBoxW1 + rotBBoxW2 - prelimOverlapPx
    const idealBoost = currentSpan > 0 ? areaWidth / currentSpan : 1.0

    const maxBoostH = Math.min(areaHeight / img1.getScaledHeight(), areaHeight / img2.getScaledHeight())
    const overlapBoost = Math.min(idealBoost, maxBoostH)

    img1.scale(finalScale1 * overlapBoost)
    img2.scale(finalScale2 * overlapBoost)

    // Recompute rotBBoxes with the boosted scale
    rotBBoxW1 = img1.getScaledWidth() * abscos + img1.getScaledHeight() * abssin
    rotBBoxH1 = img1.getScaledWidth() * abssin + img1.getScaledHeight() * abscos
    rotBBoxW2 = img2.getScaledWidth() * abscos + img2.getScaledHeight() * abssin
    rotBBoxH2 = img2.getScaledWidth() * abssin + img2.getScaledHeight() * abscos
  }
  let totalWidth = rotBBoxW1 + rotBBoxW2
  if (style == TOP_PLACEMENT_STYLE.GAP) {
    totalWidth += gapPx
  } else if (style == TOP_PLACEMENT_STYLE.OVERLAP) {
    const slantCompensation = Math.min(img1.getScaledHeight(), img2.getScaledHeight()) * abssin
    const visualOverlapPx = Math.min(img1.getScaledWidth(), img2.getScaledWidth()) * (overlapPercentage / 100)
    const overlapPx = slantCompensation + visualOverlapPx
    totalWidth -= overlapPx
  }

  const startX = (areaLeft + areaRight - totalWidth) / 2
  const img2Left = startX + rotBBoxW2 / 2
  const img1Left = startX + totalWidth - rotBBoxW1 / 2

  let img1Top, img2Top
  if (placementType === PLACEMENT_TYPES.FLOAT) {
    const centerY = (areaTop + areaBottom) / 2
    img1Top = centerY
    img2Top = centerY
  } else {
    img1Top = areaBottom - rotBBoxH1 / 2
    img2Top = areaBottom - rotBBoxH2 / 2
  }

  img1.set({
    left: img1Left,
    top: img1Top,
    angle: tiltAngle,
    originX: 'center',
    originY: 'center',
    opacity: 1,
    layerName: 'Product1',
  })
  img2.set({
    left: img2Left,
    top: img2Top,
    angle: tiltAngle,
    originX: 'center',
    originY: 'center',
    opacity: 1,
    layerName: 'Product2',
  })

  img1.set('layerId', uuidv4())
  canvas.add(img1)

  img2.set('layerId', uuidv4())
  canvas.add(img2)

  if (lightDirection === DIRECTIONS.BEHIND && placementType === PLACEMENT_TYPES.FLOAT) {
    const boundingRect = { left: Infinity, top: Infinity, right: -Infinity, bottom: -Infinity }
    images.forEach((img: any) => {
      img.setCoords()
      const bounds = img.getBoundingRect()
      boundingRect.left = Math.min(boundingRect.left, bounds.left)
      boundingRect.top = Math.min(boundingRect.top, bounds.top)
      boundingRect.right = Math.max(boundingRect.right, bounds.left + bounds.width)
      boundingRect.bottom = Math.max(boundingRect.bottom, bounds.top + bounds.height)
    })

    const group = new Group([], {
      left: boundingRect.left,
      top: boundingRect.top,
      subTargetCheck: true,
      interactive: false,
    })

    images.forEach((img: any, i) => {
      const imgGlobalLeft = img.left!
      const imgGlobalTop = img.top!
      const clonedImg = new FabricImage(img.getElement(), {
        left: imgGlobalLeft - boundingRect.left - (boundingRect.right - boundingRect.left) / 2,
        top: imgGlobalTop - boundingRect.top - (boundingRect.bottom - boundingRect.top) / 2,
        scaleX: img.scaleX,
        scaleY: img.scaleY,
        angle: img.angle,
        originX: 'center',
        originY: 'center',
        objectCaching: false,
        layerName: img.layerName,
      })
      canvas.remove(img)
      group.add(clonedImg)
    })

    group.set({
      left: boundingRect.left,
      top: boundingRect.top,
    })
    group.set('shadow', createGlowShadow(group))
    group.set('layerName', 'glowGroup')
    group.setCoords()
    canvas.add(group)
    canvas.bringObjectToFront(group)
    canvas.requestRenderAll()
  } else {
    img1.set('shadow', createShadow(img1, placementType, lightDirection))
    img2.set('shadow', createShadow(img2, placementType, lightDirection))
    canvas.renderAll()
  }
  const canvasStore = useCanvasStore()
  canvasStore.setHideLoader(true)
}

function getFloatOffsetsForPairProducts(
  width1: number,
  height1: number,
  width2: number,
  height2: number,
): { xOffsetFactor: number; yOffsetFactor: number } {
  const aspect1 = width1 / height1
  const aspect2 = width2 / height2
  if (aspect1 < 0.9 && aspect2 < 0.9) return { xOffsetFactor: -0.1, yOffsetFactor: 0 }
  if (aspect1 < 0.8 && aspect2 > 1.6) return { xOffsetFactor: -0.15, yOffsetFactor: 0.3 }
  if (aspect1 < 1.0 && aspect2 < 1.0) return { xOffsetFactor: -0.08, yOffsetFactor: 0.35 }
  if (aspect1 > 1.0 && aspect2 < 0.8) return { xOffsetFactor: -0.08, yOffsetFactor: 0.18 }
  if (aspect2 > 2.0) return { xOffsetFactor: -0.2, yOffsetFactor: 0.35 }
  if (aspect2 > 1.4) return { xOffsetFactor: -0.15, yOffsetFactor: 0.28 }
  if (aspect2 < 0.75) return { xOffsetFactor: -0.07, yOffsetFactor: 0.16 }

  return { xOffsetFactor: -0.1, yOffsetFactor: 0.2 }
}

// VERTICAL PLACEMENT OF TWO PRODUCTS
export async function renderVerticalPairPlacement(
  canvas: Canvas,
  images: FabricImage[],
  tiltAngle: number,
  lightDirection: string,
  placementType: string,
) {
  const areaHeight = PLACEMENT_AREA.bottom - PLACEMENT_AREA.top
  const areaWidth = PLACEMENT_AREA.right - PLACEMENT_AREA.left
  const centerX = (PLACEMENT_AREA.left + PLACEMENT_AREA.right) / 2

  const verticalPadding = areaHeight * 0.05
  const availableHeight = areaHeight - 2 * verticalPadding
  const desiredSpacing = 16

  const scaleFactor = 0.98

  const scaledImages = images.map((img) => {
    const heightScale = (availableHeight - desiredSpacing) / 2 / img.height!
    const widthScale = areaWidth / img.width!
    const scale = Math.min(heightScale, widthScale) * scaleFactor

    img.scale(scale)
    return {
      img,
      scaledHeight: img.getScaledHeight(),
      scaledWidth: img.getScaledWidth(),
    }
  })

  if (lightDirection === DIRECTIONS.BEHIND) {
    // Position images first
    const [top, bottom] = scaledImages
    const totalUsedHeight = top.scaledHeight + bottom.scaledHeight + desiredSpacing
    const startY = PLACEMENT_AREA.top + (areaHeight - totalUsedHeight) / 2

    const topCenterY = startY + top.scaledHeight / 2
    const bottomCenterY =
      topCenterY + top.scaledHeight / 2 + desiredSpacing + bottom.scaledHeight / 2

    const compensationFactor = 0.008

    scaledImages.forEach(({ img, scaledWidth }, index) => {
      const isTop = index === 0
      const centerY = isTop ? topCenterY : bottomCenterY
      const tiltForImage =
        tiltAngle === 0 ? 0 : getRandomNumber(tiltAngle, -10, 10) * (isTop ? 1 : -1)
      const horizontalOffset =
        tiltAngle !== 0 ? scaledWidth * compensationFactor * Math.sign(tiltForImage) : 0

      img.set({
        left: centerX + horizontalOffset,
        top: centerY,
        originX: 'center',
        originY: 'center',
        angle: tiltForImage,
        layerName: `Product${index + 1}`,
        shadow: null,
      })
    })

    // Calculate bounding box accounting for center origins
    const boundingRect = images.reduce(
      (acc, img) => {
        const actualLeft = img.left! - img.getScaledWidth() / 2
        const actualTop = img.top! - img.getScaledHeight() / 2
        return {
          left: Math.min(acc.left, actualLeft),
          top: Math.min(acc.top, actualTop),
          right: Math.max(acc.right, actualLeft + img.getScaledWidth()),
          bottom: Math.max(acc.bottom, actualTop + img.getScaledHeight()),
        }
      },
      {
        left: Infinity,
        top: Infinity,
        right: -Infinity,
        bottom: -Infinity,
      },
    )

    // Create and configure group
    const group = new Group([], {
      left: boundingRect.left,
      top: boundingRect.top,
      subTargetCheck: true,
      interactive: false,
    })

    // Add cloned images to group
    images.forEach((img: any) => {
      const imgGlobalLeft = img.left! - img.getScaledWidth() / 2
      const imgGlobalTop = img.top! - img.getScaledHeight() / 2

      const relativeLeft = imgGlobalLeft - boundingRect.left
      const relativeTop = imgGlobalTop - boundingRect.top

      const clonedImg = new FabricImage(img.getElement(), {
        left: relativeLeft,
        top: relativeTop,
        scaleX: img.scaleX,
        scaleY: img.scaleY,
        angle: img.angle,
        originX: 'left',
        originY: 'top',
        objectCaching: false,
        layerName: img.layerName,
      })

      canvas.remove(img)
      group.add(clonedImg)
    })

    // Finalize group positioning
    group.set({
      left: boundingRect.left,
      top: boundingRect.top,
    })
    group.set('shadow', createGlowShadow(group))
    group.set('layerName', 'glowGroup')
    group.setCoords()
    canvas.add(group)
    canvas.bringObjectToFront(group)
    canvas.requestRenderAll()
  } else {
    // Original shadow handling for other directions
    let shadowType = ''
    const [top, bottom] = scaledImages

    const totalUsedHeight = top.scaledHeight + bottom.scaledHeight + desiredSpacing
    const startY = PLACEMENT_AREA.top + (areaHeight - totalUsedHeight) / 2

    const topCenterY = startY + top.scaledHeight / 2
    const bottomCenterY =
      topCenterY + top.scaledHeight / 2 + desiredSpacing + bottom.scaledHeight / 2

    const compensationFactor = 0.008

    for (let index = 0; index < scaledImages.length; index++) {
      const { img, scaledWidth } = scaledImages[index]
      const isTop = index === 0
      const centerY = isTop ? topCenterY : bottomCenterY
      const tiltForImage =
        tiltAngle === 0 ? 0 : getRandomNumber(tiltAngle, -10, 10) * (isTop ? 1 : -1)
      const horizontalOffset =
        tiltAngle !== 0 ? scaledWidth * compensationFactor * Math.sign(tiltForImage) : 0

      img.set({
        left: centerX + horizontalOffset,
        top: centerY,
        originX: 'center',
        originY: 'center',
        angle: tiltForImage,
        layerName: `Product${index + 1}`,
      })
      const aspectRatio = (img.height * img.scaleY) / (img.width * img.scaleX)
      if (!shadowType) {
        if (Math.abs(img.angle % 360) > 20 && Math.abs(img.angle % 360) < 340) {
          shadowType = 'rotate'
        } else {
          shadowType = aspectRatio > 1.5 ? 'ellipse' : 'square'
        }
      }
      if (lightDirection === DIRECTIONS.TOP) {
        if (images.some((img: any) => img.isMultiple)) {
          img.shadow = createDropShadow(img, DIRECTIONS.BOTTOM)
        } else {
          img.set('layerId', uuidv4())
          const bottomPoint = getBottomPoint(img, tiltForImage)
          const shadowImage = await createContactShadow(img, shadowType, canvas, tiltForImage)
          shadowImage.set({
            globalCompositeOperation: 'multiply',
            layerId: (img as FabricImage & { layerId?: string }).layerId,
          })
          const centerShadowLeft = bottomPoint.x + shadowImage.getScaledWidth() / 2
          let offset = 0
          if (
            tiltForImage === 0 &&
            (shadowImage as FabricImage & { layerType?: string }).layerType == 'square'
          ) {
            offset = shadowImage.getScaledHeight() / 2
          }

          shadowImage.set({
            left: centerShadowLeft,
            top: bottomPoint.y + offset,
          })
          canvas.add(shadowImage)
        }

        canvas.add(img)
        img.set({ visible: true })
        canvas.requestRenderAll()
      } else {
        canvas.add(img)
        img.set('shadow', createShadow(img, placementType, lightDirection))
      }
    }

    const canvasStore = useCanvasStore()
    canvas.requestRenderAll()
    canvasStore.setHideLoader(true)
    return
  }

  const canvasStore = useCanvasStore()
  canvasStore.setHideLoader(true)
}

// STACK PLACEMENT OF TWO PRODUCTS
export function renderStackPairPlacement(
  canvas: Canvas,
  images: FabricImage[],
  tiltAngle: number,
  lightDirection: string,
  placementType: string,
): void {
  const placementArea = getPlacementBounds("stack2")
  const areaLeft = placementArea.left
  const areaRight = placementArea.right
  const areaTop = placementArea.top
  const areaBottom = placementArea.bottom
  const areaWidth = areaRight - areaLeft
  const areaHeight = areaBottom - areaTop
  const img1 = images.reduce((widest, current) =>
    current.width / current.height > widest.width / widest.height ? current : widest,
  )
  const img2 = images.filter((img) => img !== img1)[0]

  const angle1 = tiltAngle == 0 ? 0 : getRandomNumber(tiltAngle, -10, 10)
  const angle2 = tiltAngle == 0 ? 0 : getRandomNumber(tiltAngle, -10, 10)
  const angle1Rad = angle1 * Math.PI / 180
  const angle2Rad = angle2 * Math.PI / 180
  const abscos1 = Math.abs(Math.cos(angle1Rad))
  const abssin1 = Math.abs(Math.sin(angle1Rad))
  const abscos2 = Math.abs(Math.cos(angle2Rad))
  const abssin2 = Math.abs(Math.sin(angle2Rad))

  const aspect1 = img1.width / img1.height
  const aspect2 = img2.width / img2.height
  const horizontalOverlapPercent = 60
  const verticalOverlapPercent = 30

  const { heightShare1, heightShare2 } = getHeightSplitForPairWithRotationPenalty(aspect1, aspect2, angle1Rad, angle2Rad)
  const widthShareResult = getWidthSplitWithRotationPenaltyFor2Products(aspect1, aspect2, 0, angle1Rad, angle2Rad, 0.4, 0.6)

  const img1Scale = Math.min(
    (areaWidth * widthShareResult.share1) / (img1.width * abscos1 + img1.height * abssin1),
    (areaHeight * heightShare1) / (img1.width * abssin1 + img1.height * abscos1),
  )
  const img2Scale = Math.min(
    (areaWidth * widthShareResult.share2) / (img2.width * abscos2 + img2.height * abssin2),
    (areaHeight * heightShare2) / (img2.width * abssin2 + img2.height * abscos2),
  )

  img1.scale(img1Scale)
  img2.scale(img2Scale)

  const avgAbsSin = (abssin1 + abssin2) / 2

  let slantH = Math.min(img1.getScaledHeight(), img2.getScaledHeight()) * avgAbsSin
  let visOverlapH = Math.min(img1.getScaledWidth(), img2.getScaledWidth()) * (horizontalOverlapPercent / 100)
  let horizontalOverlap = slantH + visOverlapH

  let slantV = Math.min(img1.getScaledWidth(), img2.getScaledWidth()) * avgAbsSin
  let visOverlapV = Math.min(img1.getScaledHeight(), img2.getScaledHeight()) * (verticalOverlapPercent / 100)
  let verticalOverlap = slantV + visOverlapV

  const sw1 = img1.getScaledWidth()
  const sh1 = img1.getScaledHeight()
  const sw2 = img2.getScaledWidth()
  const sh2 = img2.getScaledHeight()

  const wbb1 = sw1 * abscos1 + sh1 * abssin1
  const hbb1 = sw1 * abssin1 + sh1 * abscos1
  const wbb2 = sw2 * abscos2 + sh2 * abssin2
  const hbb2 = sw2 * abssin2 + sh2 * abscos2

  const cx1 = 0
  const cy1 = 0
  const cx2 = cx1 + (wbb1 / 2 + wbb2 / 2) - horizontalOverlap
  const cy2 = cy1 - ((hbb1 / 2 + hbb2 / 2) - verticalOverlap)

  const getRotatedBBBounds = (cx: number, cy: number, w: number, h: number, angleRad: number) => {
    const abscos = Math.abs(Math.cos(angleRad))
    const abssin = Math.abs(Math.sin(angleRad))
    const wbb = w * abscos + h * abssin
    const hbb = w * abssin + h * abscos
    return { left: cx - wbb / 2, right: cx + wbb / 2, top: cy - hbb / 2, bottom: cy + hbb / 2 }
  }

  const bounds1 = getRotatedBBBounds(cx1, cy1, sw1, sh1, angle1Rad)
  const bounds2 = getRotatedBBBounds(cx2, cy2, sw2, sh2, angle2Rad)

  const groupLeft = Math.min(bounds1.left, bounds2.left)
  const groupRight = Math.max(bounds1.right, bounds2.right)
  const groupTop = Math.min(bounds1.top, bounds2.top)
  const groupBottom = Math.max(bounds1.bottom, bounds2.bottom)

  const combinedW = groupRight - groupLeft
  const combinedH = groupBottom - groupTop

  const zoom = Math.min(areaWidth / combinedW, areaHeight / combinedH)

  img1.scale(img1Scale * zoom)
  img2.scale(img2Scale * zoom)

  slantH = Math.min(img1.getScaledHeight(), img2.getScaledHeight()) * avgAbsSin
  visOverlapH = Math.min(img1.getScaledWidth(), img2.getScaledWidth()) * (horizontalOverlapPercent / 100)
  horizontalOverlap = slantH + visOverlapH

  slantV = Math.min(img1.getScaledWidth(), img2.getScaledWidth()) * avgAbsSin
  visOverlapV = Math.min(img1.getScaledHeight(), img2.getScaledHeight()) * (verticalOverlapPercent / 100)
  verticalOverlap = slantV + visOverlapV

  const rotBBoxW1 = img1.getScaledWidth() * abscos1 + img1.getScaledHeight() * abssin1
  const rotBBoxH1 = img1.getScaledWidth() * abssin1 + img1.getScaledHeight() * abscos1
  const rotBBoxW2 = img2.getScaledWidth() * abscos2 + img2.getScaledHeight() * abssin2
  const rotBBoxH2 = img2.getScaledWidth() * abssin2 + img2.getScaledHeight() * abscos2

  // Lay out the pair relative to each other first, then shift the whole group
  // so its combined bounding box sits centered (both axes) in the placement area.
  const groupCx1 = 0
  const groupCy1 = 0
  const groupCx2 = groupCx1 + (rotBBoxW1 / 2 + rotBBoxW2 / 2) - horizontalOverlap
  const groupCy2 = groupCy1 - ((rotBBoxH1 / 2 + rotBBoxH2 / 2) - verticalOverlap)

  const finalBounds1 = getRotatedBBBounds(groupCx1, groupCy1, img1.getScaledWidth(), img1.getScaledHeight(), angle1Rad)
  const finalBounds2 = getRotatedBBBounds(groupCx2, groupCy2, img2.getScaledWidth(), img2.getScaledHeight(), angle2Rad)

  const finalGroupLeft = Math.min(finalBounds1.left, finalBounds2.left)
  const finalGroupRight = Math.max(finalBounds1.right, finalBounds2.right)
  const finalGroupTop = Math.min(finalBounds1.top, finalBounds2.top)
  const finalGroupBottom = Math.max(finalBounds1.bottom, finalBounds2.bottom)

  const groupCenterX = (finalGroupLeft + finalGroupRight) / 2
  const groupCenterY = (finalGroupTop + finalGroupBottom) / 2
  const areaCenterX = (areaLeft + areaRight) / 2
  const areaCenterY = (areaTop + areaBottom) / 2

  const offsetX = areaCenterX - groupCenterX
  const offsetY = areaCenterY - groupCenterY

  const img1Left = groupCx1 + offsetX
  const img1Top = groupCy1 + offsetY
  const img2Left = groupCx2 + offsetX
  const img2Top = groupCy2 + offsetY

  img1.set({
    originX: 'center',
    originY: 'center',
    left: img1Left,
    top: img1Top,
    angle: angle1,
    layerName: 'Product1'
  })
  img1.set('shadow', createShadow(img1, 'stack', lightDirection))

  img2.set({
    originX: 'center',
    originY: 'center',
    left: img2Left,
    top: img2Top,
    angle: angle2,
    layerName: 'Product2'
  })
  img2.set('shadow', createShadow(img2, placementType, lightDirection))

  canvas.add(img1)
  canvas.add(img2)

  img1.setCoords()
  img2.setCoords()

  canvas.bringObjectToFront(img1)
  canvas.renderAll()
  
  const canvasStore = useCanvasStore()
  canvasStore.setHideLoader(true)
}

// Corner Placement for Two Products
async function renderCornerPairPlacement(
  canvas: Canvas,
  images: FabricImage[],
  angle: number = -10,
  lighting: string,
  placementType: string,
  aspectCounts: number = 0,
  areAllEdibleProducts: boolean = false
) {
  // If both products are horizontal
  if (aspectCounts == 2) {
    return renderHorizontalProductPair(canvas, images, lighting, placementType, areAllEdibleProducts)
  }

  const placementBounds = areAllEdibleProducts ? getPlacementBounds('maxProductFood') : getPlacementBounds('corner2')
  const areaLeft = placementBounds.left
  const areaTop = placementBounds.top
  const areaRight = placementBounds.right
  const areaBottom = placementBounds.bottom
  const areaWidth = areaRight - areaLeft
  const areaHeight = areaBottom - areaTop
  const tiltAngle = -10

  const img1 = images.reduce((widest, current) =>
    current.width / current.height > widest.width / widest.height ? current : widest,
  )
  const secondaryImgs = images.filter((img) => img !== img1)
  const [img2] = secondaryImgs
  const aspect1 = img1.width / img1.height
  const aspect2 = img2.width / img2.height
  const overlapPercentage = getRandomNumber(10, 0, 5)
  const angleRad = (tiltAngle * Math.PI) / 180
  // Pass img2's tilt so getWidthSplit gives it extra width to compensate for rotation
  const { share1, share2 } = getWidthSplitWithRotationPenaltyFor2Products(aspect1, aspect2, 0,0, angleRad)

  const abscos = Math.abs(Math.cos(angleRad))
  const abssin = Math.abs(Math.sin(angleRad))

  const finalScale1 = Math.min((areaWidth * share1) / img1.width, areaHeight / img1.height)

  const finalScale2 = Math.min(
    (areaWidth * share2) / (img2.width * abscos + img2.height * abssin),
    areaHeight / (img2.width * abssin + img2.height * abscos),
  )

  img1.scale(finalScale1)
  img2.scale(finalScale2)

  let rotBBoxW1 = img1.getScaledWidth()
  let rotBBoxH1 = img1.getScaledHeight()
  let rotBBoxW2 = img2.getScaledWidth() * abscos + img2.getScaledHeight() * abssin
  let rotBBoxH2 = img2.getScaledWidth() * abssin + img2.getScaledHeight() * abscos

  const slantComp12 = img2.getScaledHeight() * abssin
  const visOverlap12 = Math.min(img2.getScaledWidth(), img1.getScaledWidth()) * (overlapPercentage / 100)
  const overlapPx12 = slantComp12 + visOverlap12

  const currentSpan = rotBBoxW1 + rotBBoxW2 - overlapPx12
  const idealBoost = currentSpan > 0 ? areaWidth / currentSpan : 1.0
  const maxBoostH = Math.min(
    areaHeight / img1.getScaledHeight(),
    areaHeight / rotBBoxH2
  )

  const overlapBoost = Math.min(idealBoost, maxBoostH)

  // Apply boost
  img1.scale(finalScale1 * overlapBoost)
  img2.scale(finalScale2 * overlapBoost)

  rotBBoxW1 = img1.getScaledWidth()
  rotBBoxH1 = img1.getScaledHeight()
  rotBBoxW2 = img2.getScaledWidth() * abscos + img2.getScaledHeight() * abssin
  rotBBoxH2 = img2.getScaledWidth() * abssin + img2.getScaledHeight() * abscos

  // Recompute actual overlap pixels with the boosted scale (per-angle slant)
  const boostedSlantComp12 = img2.getScaledHeight() * abssin   // only img1 is tilted at this boundary
  const boostedVisOverlap12 = Math.min(img1.getScaledWidth(), img2.getScaledWidth()) * (overlapPercentage / 100)
  const boostedOverlapPx12 = boostedSlantComp12 + boostedVisOverlap12

  const img1CenterX = areaRight - rotBBoxW1 / 2
  const img1CenterY = areaBottom - rotBBoxH1 / 2

  const img2CenterX = img1CenterX - rotBBoxW1 / 2 - rotBBoxW2 / 2 + boostedOverlapPx12
  const img2CenterY = areaBottom - rotBBoxH2 / 2

  img1.set({
    left: img1CenterX,
    top: img1CenterY,
    angle: 0,
    originX: 'center',
    originY: 'center',
    opacity: 1,
    shadow:
      lighting === DIRECTIONS.BEHIND ? undefined : createShadow(img1, placementType, lighting),
    layerName: 'Product1',
  })

  img2.set({
    left: img2CenterX,
    top: img2CenterY,
    angle: tiltAngle,
    originX: 'center',
    originY: 'center',
    opacity: 1,
    shadow:
      lighting === DIRECTIONS.BEHIND ? undefined : createShadow(img2, placementType, lighting),
    layerName: 'Product2',
  })
  canvas.add(img2)
  canvas.add(img1)
  canvas.renderAll()

  if (lighting === DIRECTIONS.BEHIND) {
    const boundingRect = images.reduce(
      (acc, img) => {
        const actualLeft = img.left! - img.getScaledWidth() / 2 // originX is 'center'
        const actualTop = img.top // originY is 'center'
        return {
          left: Math.min(acc.left, actualLeft),
          top: Math.min(acc.top, actualTop),
          right: Math.max(acc.right, actualLeft + img.getScaledWidth()),
          bottom: Math.max(acc.bottom, actualTop + img.getScaledHeight()),
        }
      },
      {
        left: Infinity,
        top: Infinity,
        right: -Infinity,
        bottom: -Infinity,
      },
    )
    const group = new Group([], {
      left: boundingRect.left,
      subTargetCheck: true,
      interactive: false,
      originX: 'right',
      originY: 'center',
    })
    images = images.reverse()
    images.forEach((img: any) => {
      const clonedImg = new FabricImage(img.getElement(), {
        left: img.left,
        top: img.top,
        scaleX: img.scaleX,
        scaleY: img.scaleY,
        angle: img.angle,
        originX: 'center',
        originY: 'center',
        objectCaching: false,
        layerName: img.layerName,
      })

      canvas.remove(img)
      group.add(clonedImg)
    })
    group.set({
      left: boundingRect.right,
    })
    group.set('shadow', createGlowShadow(group))
    group.set('layerName', 'glowGroup')
    group.setCoords()
    canvas.add(group)
    canvas.bringObjectToFront(group)
    canvas.requestRenderAll()
  }

  const canvasStore = useCanvasStore()
  canvasStore.setHideLoader(true)
}

export async function renderHorizontalProductPair(
  canvas: Canvas,
  images: FabricImage[],
  lighting: string,
  placementType: string,
  areAllEdibleProducts: boolean = false,
  mainAngle: number = 20,
) {
  if (images.length !== 2) return

  const placementBounds = areAllEdibleProducts ? getPlacementBounds('maxProductFood') : getPlacementBounds('corner2Horizontal2')
  const areaHeight = placementBounds.bottom - placementBounds.top
  const areaWidth = placementBounds.right - placementBounds.left

  // Base reference points
  const baseRight = placementBounds.right
  const baseBottom = placementBounds.bottom
  const avilableHeightForEachProd = areaHeight / 2

  // Scale images appropriately
  const scaledImages = images.map((img) => {
    const heightScale = avilableHeightForEachProd / img.height
    const widthScale = areaWidth / img.width
    const scale = Math.min(heightScale, widthScale)

    img.scale(scale)
    return {
      img,
      scaledHeight: img.getScaledHeight(),
      scaledWidth: img.getScaledWidth(),
      aspectRatio: img.height / img.width,
      widthToHeightRatio: img.width / img.height,
    }
  })

  const [img1Data, img2Data] = [...scaledImages].sort((a, b) => b.widthToHeightRatio - a.widthToHeightRatio)
  const img1 = img1Data.img
  const img2 = img2Data.img

  // For wide products, calculate how "wide" they are (width-to-height ratio)
  // This helps adjust placement for extremely wide vs moderately wide products
  const wideRatio1 = img1Data.widthToHeightRatio
  const wideRatio2 = img2Data.widthToHeightRatio

  // Determine if products are extremely wide (like keyboards) or moderately wide
  const isExtremelyWide1 = wideRatio1 > 3.0
  const isExtremelyWide2 = wideRatio2 > 3.0
  const isVeryWide1 = wideRatio1 > 2.0
  const isVeryWide2 = wideRatio2 > 2.0

  // === ANGLE OPTIMIZATION FOR WIDE PRODUCTS ===
  // Wide products look better with smaller angles
  // The wider the product, the smaller the ideal angle
  const firstAngle = Math.max(mainAngle - (isExtremelyWide1 ? 10 : isVeryWide1 ? 8 : 6), 10)

  // For the second product, increase angle slightly more than default
  // This creates better visual separation between wide products
  const secondAngle = Math.max(mainAngle + (isExtremelyWide2 ? 12 : isVeryWide2 ? 15 : 18), 20)

  // === OFFSET OPTIMIZATION FOR WIDE PRODUCTS ===
  // Calculate optimal offsets based on product width
  // Extremely wide products need larger horizontal offsets

  // Apply smaller vertical offset for wide products
  // const verticalOffset1 =  * (isExtremelyWide1 ? 0.6 : isVeryWide1 ? 0.7 : 0.8)
  const verticalOffset1 = img1Data.scaledHeight * (isExtremelyWide1 ? 0.06 : isVeryWide1 ? 0.07 : 0.08)
  const verticalOffset2 = img2Data.scaledHeight * (isExtremelyWide2 ? 0.06 : isVeryWide2 ? 0.07 : 0.08)

  // Horizontal offset increases with product width
  const horizontalOffset1 = img1Data.scaledWidth * (isExtremelyWide1 ? 0.06 : isVeryWide1 ? 0.08 : 0.1)

  // Second product horizontal offset is smaller to create overlap
  const horizontalOffset2 = img2Data.scaledWidth * (isExtremelyWide2 ? 0.03 : isVeryWide2 ? 0.04 : 0.05)

  // === BOOST: how much can each image scale from its pivot before crossing a boundary? ===
  // Pivot for both images: (baseRight + hOffset, baseBottom + vOffset) with originX:'right', originY:'bottom'
  const px1 = baseRight + horizontalOffset1
  const py1 = baseBottom + verticalOffset1
  const px2 = baseRight + horizontalOffset2
  const py2 = baseBottom + verticalOffset2

  const rad1 = (firstAngle * Math.PI) / 180
  const cos1 = Math.cos(rad1), sin1 = Math.sin(rad1)
  const rad2 = (secondAngle * Math.PI) / 180
  const cos2 = Math.cos(rad2), sin2 = Math.sin(rad2)

  const W1 = img1Data.scaledWidth, H1 = img1Data.scaledHeight
  const W2 = img2Data.scaledWidth, H2 = img2Data.scaledHeight
  const areaLeft = placementBounds.left
  const areaTop = placementBounds.top

  // When rotated from the bottom right the left most point will be bottom left and the top most point will be top left
  // The distance along the y axis from the origin to top left is W1 * sin1 + H1 * cos1
  // The distance along the x axis from the origin to left most point(Bottom left) is W1 * cos1
  // So we decide the scale based on these points
  const fitScale1 = Math.min(
    (px1 - areaLeft) / (W1 * cos1),
    (py1 - areaTop) / (W1 * sin1 + H1 * cos1),
  )
  const fitScale2 = Math.min(
    (px2 - areaLeft) / (W2 * cos2),
    (py2 - areaTop) / (W2 * sin2 + H2 * cos2),
  )

  // We want to scale both images by the same amount so that one do not dominate the other
  const finalFitScale = Math.min(fitScale1, fitScale2)

  const initialScale1 = img1Data.scaledWidth / img1.width
  const initialScale2 = img2Data.scaledWidth / img2.width
  img1.scale(initialScale1 * finalFitScale)
  img2.scale(initialScale2 * finalFitScale)


  // === POSITION ===
  img1.set({
    left: px1,
    top: py1,
    angle: firstAngle,
    originX: 'right',
    originY: 'bottom',
    opacity: 1,
    shadow: null,
    layerName: 'Product1',
  })
  canvas.add(img1)

  img2.set({
    left: px2,
    top: py2,
    angle: secondAngle,
    originX: 'right',
    originY: 'bottom',
    opacity: 1,
    shadow: null,
    layerName: 'Product2',
  })

  canvas.add(img2)
  canvas.bringObjectToFront(img2)


  // === TOP LIGHTING SPECIAL SHADOW HANDLING FOR WIDE PRODUCTS ===
  if (lighting === DIRECTIONS.TOP) {
    canvas.remove(img1)
    canvas.remove(img2)
    const imgDataList = [
      {
        img: img1,
        left: px1,
        top: py1,
        angle: firstAngle,
      },
      {
        img: img2,
        left: px2,
        top: py2,
        angle: secondAngle,
      },
    ]

    for (let index = 0; index < imgDataList.length; index++) {
      const { img, left, top, angle } = imgDataList[index]
      // For wide products, optimize shadow type
      const widthToHeightRatio = (img.width! * img.scaleX!) / (img.height! * img.scaleY!)

      // Wide products should always use 'wide' shadow type
      // Extremely wide products need special shadow handling
      const shadowType = widthToHeightRatio > 3.0 ? 'ultra-wide' : 'wide'

      if (images.some((img: any) => img.isMultiple)) {
        img.shadow = createDropShadow(img, DIRECTIONS.BOTTOM)
      } else {
        img.set('layerId', uuidv4())
        const bottomPoint = getBottomPoint(img, angle)
        const shadowImage = await createContactShadow(img, shadowType, canvas, angle)
        shadowImage.set({
          globalCompositeOperation: 'multiply',
          layerId: (img as FabricImage & { layerId?: string }).layerId,
        })
        const centerShadowLeft = bottomPoint.x + shadowImage.getScaledWidth() / 2
        let offset = 0
        if (angle === 0) {
          offset = shadowImage.getScaledHeight() / 2
        }

        shadowImage.set({
          left: centerShadowLeft,
          top: bottomPoint.y + offset,
        })
        canvas.add(shadowImage)
      }

      canvas.add(img)
      img.set({ visible: true })
      canvas.requestRenderAll()
    }

    const canvasStore = useCanvasStore()
    canvas.requestRenderAll()
    canvasStore.setHideLoader(true)
    return
  } else if (lighting === DIRECTIONS.BEHIND) {
    // Compute bounding box of all images
    const boundingRect = images.reduce(
      (acc, img) => {
        const { left, top } = img.getBoundingRect()

        return {
          left: Math.min(acc.left, left),
          top: Math.min(acc.top, top),
          right: Math.max(acc.right, left + img.getScaledWidth()),
          bottom: Math.max(acc.bottom, top + img.getScaledHeight()),
        }
      },
      {
        left: Infinity,
        top: Infinity,
        right: -Infinity,
        bottom: -Infinity,
      },
    )

    // Create group and add accurately positioned clones
    const group = new Group([], {
      left: boundingRect.left,
      top: boundingRect.top,
      originX: 'left',
      originY: 'top',
      subTargetCheck: true,
      interactive: false,
    })

    images.forEach((img: any) => {
      const center = img.getPointByOrigin('left', 'top')

      const relativeLeft = center.x - boundingRect.left
      const relativeTop = center.y - boundingRect.top

      const cloned = new FabricImage(img.getElement(), {
        left: relativeLeft,
        top: relativeTop,
        scaleX: img.scaleX,
        scaleY: img.scaleY,
        angle: img.angle,
        originX: 'left',
        originY: 'top',
        objectCaching: false,
        layerName: img.layerName,
      })

      canvas.remove(img)
      group.add(cloned)
    })
    group.set({
      left: boundingRect.left,
      top: boundingRect.top,
    })
    group.setCoords()
    group.set('shadow', createGlowShadow(group))
    group.set('layerName', 'glowGroup')
    canvas.add(group)
    canvas.bringObjectToFront(group)
    canvas.requestRenderAll()
  }
  const canvasStore = useCanvasStore()
  canvasStore.setHideLoader(true)
}

function getRawConstraints(aspect: number) {
  if (aspect >= 2.0) return { w: 0.75, h: 0.55 } // Ultra wide
  if (aspect >= 1.5) return { w: 0.7, h: 0.6 } // Very horizontal
  if (aspect >= 1.1) return { w: 0.65, h: 0.75 } // Mildly horizontal
  if (aspect >= 0.9) return { w: 0.6, h: 0.95 }
  if (aspect >= 0.75) return { w: 0.55, h: 0.95 } // Slightly vertical / near-square
  if (aspect >= 0.5) return { w: 0.6, h: 0.9 } // Vertical
  return { w: 0.5, h: 0.95 } // Extremely vertical
}

function getRawFrontConstraints(aspect: number) {
  if (aspect >= 2.0) return { w: 0.65, h: 0.45 } // Ultra wide
  if (aspect >= 1.5) return { w: 0.6, h: 0.5 } // Very horizontal
  if (aspect >= 1.1) return { w: 0.55, h: 0.65 } // Mildly horizontal
  if (aspect >= 0.75) return { w: 0.55, h: 0.85 } // Slightly vertical / near-square
  if (aspect >= 0.5) return { w: 0.5, h: 0.75 } // Vertical
  return { w: 0.4, h: 0.85 } // Extremely vertical
}

const reordingImages = (images: FabricImage[]): FabricImage[] => {
  // Set same aspect ratio images in the bottom
  const aspectRatios = images.map((img) => img.width / img.height)
  const pairs = [
    { pair: [0, 1], diff: Math.abs(aspectRatios[0] - aspectRatios[1]) },
    { pair: [0, 2], diff: Math.abs(aspectRatios[0] - aspectRatios[2]) },
    { pair: [1, 2], diff: Math.abs(aspectRatios[1] - aspectRatios[2]) },
  ]
  // Find the pair with the smallest aspect ratio difference
  const bestPair = pairs.reduce((prev, curr) => (curr.diff < prev.diff ? curr : prev))
  const [i1, i2] = bestPair.pair
  const i3 = [0, 1, 2].find((i) => i !== i1 && i !== i2)!
  // Reorder: bottom row first, top last
  images = [images[i3], images[i1], images[i2]]
  return images
}

const classifyAspectRatio = (width: number, height: number) => {
  const ratio = width / height
  if (ratio > 1.6) return 'horizontal'
  if (ratio > 1.2 && ratio <= 1.6) return 'box_horizontal'
  if (ratio > 0.833 && ratio < 1.2) return 'square'
  if (ratio < 0.625) return 'vertical'
  return 'box_vertical'
}

const getCornerScalingFactors = (
  index: number,
  aspectRatioType: string,
  angle: number,
  scalingIndex: number,
) => {
  // Default scaling factors
  let widthScale = 1
  let heightScale = 1
  let isHorizontal = false
  switch (aspectRatioType) {
    case 'horizontal':
      widthScale = Math.abs(angle) > 0 ? (index === 1 ? 0.85 : 0.9) : 0.9
      heightScale = Math.abs(angle) > 0 ? (index === 1 ? 0.25 : 0.3) : 0.3
      isHorizontal = true
      break
  }
  return { widthScale, heightScale, isHorizontal }
}

function getRawCornerConstraints(aspect: number, shuffle: number) {
  if (aspect >= 2.0) return shuffle === 2 ? { w: 0.7, h: 0.5 } : { w: 0.6, h: 0.4 } // Ultra wide
  if (aspect >= 1.5) return shuffle === 2 ? { w: 0.65, h: 0.55 } : { w: 0.55, h: 0.45 } // Very horizontal
  if (aspect >= 1.1) return shuffle === 2 ? { w: 0.6, h: 0.7 } : { w: 0.45, h: 0.6 } // Mildly horizontal
  if (aspect >= 0.75) return shuffle === 2 ? { w: 0.55, h: 0.9 } : { w: 0.45, h: 0.8 } // Slightly vertical / near-square
  if (aspect >= 0.5) return shuffle === 2 ? { w: 0.55, h: 0.85 } : { w: 0.45, h: 0.8 } // Vertical
  return shuffle === 2 ? { w: 0.5, h: 0.95 } : { w: 0.4, h: 0.8 } // Extremely vertical
}

function getRawFloatConstraints(aspect: number, angle: number) {
  if (aspect >= 2.0) return angle >= 3 ? { w: 0.55, h: 0.45 } : { w: 0.6, h: 0.5 } // Ultra wide
  if (aspect >= 1.5) return angle >= 3 ? { w: 0.45, h: 0.45 } : { w: 0.5, h: 0.5 } // Very horizontal
  if (aspect >= 1.1) return angle >= 3 ? { w: 0.45, h: 0.55 } : { w: 0.5, h: 0.6 } // Mildly horizontal
  if (aspect >= 0.75) return angle >= 3 ? { w: 0.45, h: 0.5 } : { w: 0.5, h: 0.5 } // Slightly vertical / near-square
  if (aspect >= 0.5) return angle >= 3 ? { w: 0.55, h: 0.6 } : { w: 0.6, h: 0.65 } // Vertical
  return angle >= 3 ? { w: 0.45, h: 0.75 } : { w: 0.5, h: 0.75 } // Extremely vertical
}

function getRawThreeFrontConstraints(aspect: number, angle: number) {
  if (aspect >= 2.0) return angle >= 3 ? { w: 0.55, h: 0.45 } : { w: 0.6, h: 0.5 } // Ultra wide
  if (aspect >= 1.5) return angle >= 3 ? { w: 0.45, h: 0.45 } : { w: 0.5, h: 0.5 } // Very horizontal
  if (aspect >= 1.1) return angle >= 3 ? { w: 0.45, h: 0.55 } : { w: 0.5, h: 0.6 } // Mildly horizontal
  if (aspect >= 0.9) return angle >= 3 ? { w: 0.45, h: 0.4 } : { w: 0.5, h: 0.4 } // Slightly vertical / near-square
  if (aspect >= 0.75) return angle >= 3 ? { w: 0.5, h: 0.55 } : { w: 0.55, h: 0.6 } // Slightly vertical / near-square
  if (aspect >= 0.5) return angle >= 3 ? { w: 0.55, h: 0.6 } : { w: 0.6, h: 0.7 } // Vertical
  return angle >= 3 ? { w: 0.45, h: 0.75 } : { w: 0.5, h: 0.75 } // Extremely vertical
}

async function addStaircaseShadow(
  img: FabricImage,
  canvas: Canvas,
  angle: number,
  hasMultipleProducts: boolean,
): Promise<void> {
  if (hasMultipleProducts) {
    img.shadow = createDropShadow(img, DIRECTIONS.BOTTOM)
  } else {
    const shadowType = 'rotate'
    const shadowImage = await createContactShadow(img, shadowType, canvas, angle)
    shadowImage.set('globalCompositeOperation', 'multiply')
    let offsetY = 0
    if ((shadowImage as FabricImage & { layerType?: string }).layerType == 'ellipse') {
      offsetY = shadowImage.getScaledHeight() / 2
    }
    const shape = getBottomShapeLabel(img)
    const { leftBottom, rightBottom, centerBottom } = getBottomCanvasPoints(img, shape)
    let bottomX, bottomY
    if (angle < 0) {
      bottomX = leftBottom.x + shadowImage.getScaledWidth() / 2
      bottomY = leftBottom.y
    } else if (angle > 0) {
      bottomX = rightBottom.x - shadowImage.getScaledWidth() / 2
      bottomY = rightBottom.y
    } else {
      bottomX = shadowImage.left
      if (shape == 'pouch') {
        bottomY = leftBottom.y + shadowImage.getScaledHeight() / 2
      } else if (shape == 'wavy') {
      bottomY =
        centerBottom.y +
        shadowImage.getScaledHeight() / 2 -
        offsetY -
        getBottomWaveCurvePoint(img)
    } else if (shape == 'tilted') {
      bottomY = rightBottom.y + shadowImage.getScaledHeight() * 0.2
      } else {
        bottomY = centerBottom.y + shadowImage.getScaledHeight() / 2 - offsetY
      }
    }
    shadowImage.set({
      left: bottomX,
      top: bottomY,
    })
    canvas.add(shadowImage)
    canvas.moveObjectTo(shadowImage, canvas.getObjects().indexOf(img))
  }
}
